/*! Copyright (c) 2023 Olo Inc. All rights reserved. This software is made available under the Olo license (see LICENSE file). */
import { css, LitElement, html } from "lit";
import { property, query, customElement, state } from "lit/decorators.js";
import { live } from "lit/directives/live.js";
import { createRef, ref } from "lit/directives/ref.js";
const styles$4 = css`
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  :host {
    --olo-checkbox-size: 13px;
    --olo-checkbox-spacing: 16px;
    --olo-checkbox-border: 1px solid #dadada;
    --olo-checkbox-border-radius: 3px;
    --olo-checkbox-background: #ffffff;
    --olo-checkbox-text-color: #5b5959;
    --olo-checkbox-label-color: #111111;
    --olo-checkbox-font-family: Inter, sans-serif;
    --olo-checkbox-label-font-size: 16px;
    --olo-checkbox-text-font-size: 14.22px;
    --olo-checkbox-disclaimer-font-size: 12.64px;

    font-family: var(--olo-checkbox-font-family);
  }

  section {
    display: flex;
    flex-direction: column;
    gap: var(--olo-checkbox-spacing);
    padding: var(--olo-checkbox-spacing);
    background: var(--olo-checkbox-background);
    border: var(--olo-checkbox-border);
    border-radius: var(--olo-checkbox-border-radius);
    text-align: left;
  }

  section > *:not(label) {
    padding-left: calc(var(--olo-checkbox-size) / 2 + var(--olo-checkbox-spacing));
  }

  label {
    font-size: var(--olo-checkbox-label-font-size);
    color: var(--olo-checkbox-label-color);
    display: flex;
    align-items: center;
    gap: calc(var(--olo-checkbox-spacing) / 2);
  }

  input[type='checkbox'] {
    width: var(--olo-checkbox-size);
    height: var(--olo-checkbox-size);
  }

  p {
    margin-top: -8px;
    font-size: var(--olo-checkbox-text-font-size);
    color: var(--olo-checkbox-text-color);
    line-height: 1.5;
  }

  small {
    font-size: var(--olo-checkbox-disclaimer-font-size);
    color: var(--olo-checkbox-text-color);
    line-height: 1.5;
  }

  hr {
    border-color: var(--olo-checkbox-border-color);
    border-width: 0.5px;
  }

  a {
    color: inherit;
  }
`;
var __defProp$4 = Object.defineProperty;
var __getOwnPropDesc$4 = Object.getOwnPropertyDescriptor;
var __decorateClass$4 = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc$4(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp$4(target, key, result);
  return result;
};
const cssVariablesMap$1 = {
  size: "--olo-checkbox-size",
  spacing: "--olo-checkbox-spacing",
  border: "--olo-checkbox-border",
  borderRadius: "--olo-checkbox-border-radius",
  background: "--olo-checkbox-background",
  textColor: "--olo-checkbox-text-color",
  labelColor: "--olo-checkbox-label-color",
  fontFamily: "--olo-checkbox-font-family",
  labelFontSize: "--olo-checkbox-label-font-size",
  textFontSize: "--olo-checkbox-text-font-size",
  disclaimerFontSize: "--olo-checkbox-disclaimer-font-size"
};
function assertCustomStyle$1(style) {
  if (typeof style !== "string" || !cssVariablesMap$1.hasOwnProperty(style)) {
    throw new TypeError(`${style} is not a customizable style of CreateAccountCheckbox`);
  }
}
function optionsMatch(options) {
  if (typeof (options == null ? void 0 : options.basketId) === "string" && !!(options == null ? void 0 : options.basketId.trim().length) && typeof (options == null ? void 0 : options.apiUrl) === "string" && !!(options == null ? void 0 : options.apiUrl.trim().length) && typeof (options == null ? void 0 : options.clientId) === "string" && !!(options == null ? void 0 : options.clientId.trim().length)) {
    return true;
  }
  console.error("CreateAccountCheckbox received bad options and cannot construct.");
  console.error(
    `Options passed: \`basketId\` "${options == null ? void 0 : options.basketId}", \`apiUrl\` "${options == null ? void 0 : options.apiUrl}", and \`clientId\` "${options == null ? void 0 : options.clientId}".`
  );
  return false;
}
let CreateAccountCheckbox = class extends LitElement {
  constructor(options) {
    super();
    this.checked = true;
    this.isLoyalty = options.isLoyalty;
    if (!optionsMatch(options))
      return;
    this.basketId = options.basketId;
    this.apiUrl = options.apiUrl;
    this.clientId = options.clientId;
    this.onChecked = options.onChecked;
  }
  render() {
    var _a, _b, _c, _d, _e, _f, _g;
    return html`
      <section part="main">
        <label>
          <input
            type="checkbox"
            role="checkbox"
            name="Create Olo Account Checkbox"
            @change=${this.setChecked}
            .checked=${this.checked}
          />${(_a = this.cdnMessaging) == null ? void 0 : _a.label}
        </label>

        <p data-testid="olo-checkbox-description" part="description">
          ${(_b = this.cdnMessaging) == null ? void 0 : _b.mainDescription}
        </p>

        ${this.checked ? html`
              <hr />
              <small role="alert" data-olo-account-disclaimer part="disclaimer">
                ${(_c = this.cdnMessaging) == null ? void 0 : _c.checkedDisclaimer1}
                <a
                  href="${(_d = this.cdnMessaging) == null ? void 0 : _d.termsOfServiceUrl}"
                  target="_blank"
                  rel="nofollow noopener"
                  title="Olo's Terms of Service"
                  >Terms of Service</a
                >${(_e = this.cdnMessaging) == null ? void 0 : _e.checkedDisclaimer2}
                <a
                  href="${(_f = this.cdnMessaging) == null ? void 0 : _f.privacyNoticeUrl}"
                  target="_blank"
                  rel="nofollow noopener"
                  title="Olo's Privacy Notice"
                  >Privacy Notice</a
                >
                ${(_g = this.cdnMessaging) == null ? void 0 : _g.checkedDisclaimer3}
              </small>
            ` : html``}
      </section>
    `;
  }
  async sendOptInRequest() {
    try {
      const response = await fetch(`${this.apiUrl}/optin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          basketGuid: this.basketId,
          optIn: this.checked,
          clientId: this.clientId
        })
      });
      if (response.status !== 200) {
        console.error(response.statusText);
      }
    } catch (error) {
      console.warn("Error transmitting opt-in status to Olo.");
      console.error(error);
    }
  }
  setChecked(event) {
    var _a;
    this.checked = event.target.checked;
    (_a = this.onChecked) == null ? void 0 : _a.call(this, this.checked);
    this.sendOptInRequest();
  }
  async connectedCallback() {
    super.connectedCallback();
    this.sendOptInRequest();
    const url = this.isLoyalty ? "https://static.olocdn.net/web-client/olo-auth-service-app/optInCheckboxMessaging-loyalty.json" : "https://static.olocdn.net/web-client/olo-auth-service-app/optInCheckboxMessaging.json";
    const cdnFetch = await fetch(url);
    const cdnMessaging = await cdnFetch.json();
    this.cdnMessaging = cdnMessaging;
    this.render();
  }
  click() {
    var _a;
    (_a = this.input) == null ? void 0 : _a.click();
  }
  focus(options) {
    var _a;
    (_a = this.input) == null ? void 0 : _a.focus(options);
  }
  blur() {
    var _a;
    (_a = this.input) == null ? void 0 : _a.blur();
  }
  attach(targetElement, position = "after") {
    this.detach();
    if (position === "before")
      targetElement.prepend(this);
    if (position === "inside")
      targetElement.append(this);
    if (position === "after")
      targetElement.after(this);
  }
  detach() {
    this.remove();
  }
  setStyles(customStyles) {
    try {
      for (const style in customStyles) {
        if (customStyles.hasOwnProperty(style)) {
          assertCustomStyle$1(style);
          const variable = cssVariablesMap$1[style];
          const value = customStyles[style];
          if (value) {
            this.style.setProperty(variable, value);
          }
        }
      }
    } catch (error) {
      console.error(error);
    }
  }
};
CreateAccountCheckbox.styles = styles$4;
__decorateClass$4([
  property({ type: Object })
], CreateAccountCheckbox.prototype, "cdnMessaging", 2);
__decorateClass$4([
  query('input[type="checkbox"]')
], CreateAccountCheckbox.prototype, "input", 2);
__decorateClass$4([
  property({ type: Boolean, reflect: true })
], CreateAccountCheckbox.prototype, "checked", 2);
CreateAccountCheckbox = __decorateClass$4([
  customElement("create-account-checkbox")
], CreateAccountCheckbox);
const styles$3 = css`
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  :host {
    --olo-mfa-gap: 8px;
    --olo-mfa-padding: 14px 8px;
    --olo-mfa-font-size: 16px;
    --olo-mfa-font-weight: 600;
    --olo-mfa-font-color: #5b5959;
    --olo-mfa-font-family: 'Inter', sans-serif;
    --olo-mfa-background: #ffffff;
    --olo-mfa-primary-color: #006ad1;
    --olo-mfa-border-color: #dadada;
    --olo-mfa-border-color-invalid: #b53b39;
    --olo-mfa-border-width: 1.5px;
    --olo-mfa-border-radius: 3px;
  }

  ::selection {
    background: #2c68ca;
    color: white;
  }

  form {
    display: flex;
    flex-direction: column;
    gap: var(--olo-mfa-gap);
    max-inline-size: min-content;
  }

  fieldset {
    margin: 0;
    padding: 0;
    border: none;
    display: flex;
    flex-wrap: nowrap;
    gap: var(--olo-mfa-gap);
    max-inline-size: max-content;
    background: var(--olo-mfa-background);
  }

  legend {
    block-size: 0;
    visibility: hidden;
  }

  input {
    width: auto;
    outline: none;
    overflow: hidden;
    text-align: center;
    transition: border 250ms;
    padding: var(--olo-mfa-padding);
    color: var(--olo-mfa-font-color);
    font-size: var(--olo-mfa-font-size);
    font-weight: var(--mfa-font-weight);
    font-family: var(--olo-mfa-font-family);
    border-radius: var(--olo-mfa-border-radius);
    max-width: calc(var(--olo-mfa-font-size) * 3);
    border: var(--olo-mfa-border-width) solid var(--olo-mfa-border-color);
  }

  input:focus {
    border-color: var(--olo-mfa-primary-color);
  }

  input:user-invalid:not(:focus) {
    border-color: var(--olo-mfa-border-color-invalid);
  }

  input:disabled {
    cursor: not-allowed;
  }
`;
var __defProp$3 = Object.defineProperty;
var __getOwnPropDesc$3 = Object.getOwnPropertyDescriptor;
var __decorateClass$3 = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc$3(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp$3(target, key, result);
  return result;
};
var __accessCheck$1 = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet = (obj, member, getter) => {
  __accessCheck$1(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd$1 = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet = (obj, member, value, setter) => {
  __accessCheck$1(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var __privateMethod$1 = (obj, member, method) => {
  __accessCheck$1(obj, member, "access private method");
  return method;
};
var _inputs, _getSubmitButton, getSubmitButton_fn, _handleErrorSlot, handleErrorSlot_fn, _resetInputValue, resetInputValue_fn, _focusEmptyInput, focusEmptyInput_fn, _focusPreviousInput, focusPreviousInput_fn, _focusNextInput, focusNextInput_fn, _handleFocus, handleFocus_fn, _handleKeyDown, handleKeyDown_fn, _handleInvalid, handleInvalid_fn, _handleInput, handleInput_fn, _handleCodeComplete, handleCodeComplete_fn, _handleSubmit, handleSubmit_fn, _handlePaste, handlePaste_fn;
function isElement$1(element) {
  return element && element instanceof HTMLElement;
}
function isInput(element) {
  return isElement$1(element) && typeof element.value === "string";
}
const cssVariablesMap = {
  gap: "--olo-mfa-gap",
  padding: "--olo-mfa-padding",
  fontSize: "--olo-mfa-font-size",
  fontWeight: "--olo-mfa-font-weight",
  fontColor: "--olo-mfa-font-color",
  fontFamily: "--olo-mfa-font-family",
  background: "--olo-mfa-background",
  primaryColor: "--olo-mfa-primary-color",
  borderColor: "--olo-mfa-border-color",
  borderWidth: "--olo-mfa-border-width",
  borderRadius: "--olo-mfa-border-radius",
  borderColorInvalid: "--olo-mfa-border-color-invalid"
};
function assertCustomStyle(style) {
  if (typeof style !== "string" || !cssVariablesMap.hasOwnProperty(style)) {
    console.error(style);
    throw new TypeError("Invalid customizable style of OloCodeVerification");
  }
}
let OloCodeVerification = class extends LitElement {
  constructor(options) {
    var _a;
    super();
    __privateAdd$1(this, _getSubmitButton);
    __privateAdd$1(this, _handleErrorSlot);
    __privateAdd$1(this, _resetInputValue);
    __privateAdd$1(this, _focusEmptyInput);
    __privateAdd$1(this, _focusPreviousInput);
    __privateAdd$1(this, _focusNextInput);
    __privateAdd$1(this, _handleFocus);
    __privateAdd$1(this, _handleKeyDown);
    __privateAdd$1(this, _handleInvalid);
    __privateAdd$1(this, _handleInput);
    __privateAdd$1(this, _handleCodeComplete);
    __privateAdd$1(this, _handleSubmit);
    __privateAdd$1(this, _handlePaste);
    __privateAdd$1(this, _inputs, []);
    this.codeLength = 6;
    this.code = [];
    this.codeLength = (_a = options == null ? void 0 : options.codeLength) != null ? _a : this.codeLength;
    this.onCodeVerification = options == null ? void 0 : options.onCodeVerification;
  }
  get inputs() {
    return __privateGet(this, _inputs);
  }
  setStyles(customStyles = {}) {
    try {
      for (const style in customStyles) {
        if (customStyles.hasOwnProperty(style)) {
          assertCustomStyle(style);
          const variable = cssVariablesMap[style];
          const value = customStyles[style];
          if (variable && value) {
            this.style.setProperty(variable, value);
          }
        }
      }
    } catch (error) {
      console.error(error);
    }
  }
  reset() {
    var _a, _b;
    (_a = this.form) == null ? void 0 : _a.reset();
    (_b = __privateGet(this, _inputs).at(0)) == null ? void 0 : _b.focus();
    this.code = new Array(this.codeLength).fill("");
  }
  enableInputs() {
    __privateGet(this, _inputs).forEach((input) => input.disabled = false);
  }
  disableInputs() {
    __privateGet(this, _inputs).forEach((input) => input.disabled = true);
  }
  attach(targetElement, position = "after") {
    this.detach();
    if (position === "before")
      targetElement.prepend(this);
    if (position === "inside")
      targetElement.append(this);
    if (position === "after")
      targetElement.after(this);
  }
  detach() {
    this.remove();
  }
  async focus() {
    await __privateMethod$1(this, _focusEmptyInput, focusEmptyInput_fn).call(this);
  }
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("paste", __privateMethod$1(this, _handlePaste, handlePaste_fn));
    this.code = new Array(this.codeLength).fill("");
    this.updateComplete.then(() => {
      var _a, _b;
      const inputs = (_b = (_a = this.form) == null ? void 0 : _a.querySelectorAll("input")) != null ? _b : [];
      __privateSet(this, _inputs, Array.from(inputs));
    });
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("paste", __privateMethod$1(this, _handlePaste, handlePaste_fn));
  }
  render() {
    return html`
      <form class="olo-verification-form" @submit=${__privateMethod$1(this, _handleSubmit, handleSubmit_fn)}>
        <fieldset>
          <legend>Two-factor authentication code</legend>
          ${this.code.map(
      (value, index) => html`<input
              .value=${live(value)}
              required
              type="tel"
              inputmode="numeric"
              minlength="1"
              maxlength="1"
              pattern="[0-9]"
              spellcheck="false"
              autocorrect="off"
              autocapitalize="off"
              autocomplete="one-time-code"
              aria-invalid="false"
              aria-label="Enter verification code. Digit ${index + 1}."
              @focus=${__privateMethod$1(this, _handleFocus, handleFocus_fn)}
              @keydown=${__privateMethod$1(this, _handleKeyDown, handleKeyDown_fn)}
              @invalid=${__privateMethod$1(this, _handleInvalid, handleInvalid_fn)}
              @input=${(event) => __privateMethod$1(this, _handleInput, handleInput_fn).call(this, event, index)}
            />`
    )}
        </fieldset>

        <slot name="error-message" @slotchange=${__privateMethod$1(this, _handleErrorSlot, handleErrorSlot_fn)}></slot>

        <slot name="secondary"></slot>

        <slot @click=${() => {
      var _a;
      return (_a = this.form) == null ? void 0 : _a.requestSubmit();
    }}></slot>
      </form>
    `;
  }
};
_inputs = /* @__PURE__ */ new WeakMap();
_getSubmitButton = /* @__PURE__ */ new WeakSet();
getSubmitButton_fn = function() {
  var _a;
  return (_a = this.submitter) == null ? void 0 : _a.assignedElements({ flatten: true }).find((el) => el.tagName.toLowerCase() === "button");
};
_handleErrorSlot = /* @__PURE__ */ new WeakSet();
handleErrorSlot_fn = function(event) {
  var _a;
  const errorSlot = event.target;
  const fieldset = (_a = this.form) == null ? void 0 : _a.querySelector("fieldset");
  if (fieldset && errorSlot) {
    const uniqueId = Date.now().toString();
    fieldset.setAttribute("aria-describedby", uniqueId);
    errorSlot.id = uniqueId;
  }
};
_resetInputValue = /* @__PURE__ */ new WeakSet();
resetInputValue_fn = function(input) {
  if (isInput(input)) {
    input.value = "";
    const index = __privateGet(this, _inputs).indexOf(input);
    this.code[index] = "";
  }
};
_focusEmptyInput = /* @__PURE__ */ new WeakSet();
focusEmptyInput_fn = async function() {
  var _a;
  await this.updateComplete;
  const firstEmptyInput = __privateGet(this, _inputs).find((input) => !input.value);
  if (firstEmptyInput) {
    firstEmptyInput.focus();
  } else if (__privateMethod$1(this, _getSubmitButton, getSubmitButton_fn).call(this)) {
    (_a = __privateGet(this, _inputs).at(__privateGet(this, _inputs).length - 1)) == null ? void 0 : _a.focus();
  }
};
_focusPreviousInput = /* @__PURE__ */ new WeakSet();
focusPreviousInput_fn = function(target, previousTarget) {
  if (isInput(target)) {
    previousTarget = previousTarget != null ? previousTarget : target.previousElementSibling;
    isInput(previousTarget) && previousTarget.focus();
    return previousTarget;
  }
  return void 0;
};
_focusNextInput = /* @__PURE__ */ new WeakSet();
focusNextInput_fn = function(target, nextTarget) {
  if (isInput(target)) {
    nextTarget = nextTarget != null ? nextTarget : target.nextElementSibling;
    isInput(nextTarget) && nextTarget.focus();
    return nextTarget;
  }
  return void 0;
};
_handleFocus = /* @__PURE__ */ new WeakSet();
handleFocus_fn = function({ target }) {
  if (isInput(target) && target.value !== "") {
    target.select();
  }
};
_handleKeyDown = /* @__PURE__ */ new WeakSet();
handleKeyDown_fn = async function({ key, target }) {
  var _a;
  switch (key) {
    case "Enter":
      (_a = this.form) == null ? void 0 : _a.requestSubmit();
      break;
    case "Backspace":
      if (!isInput(target))
        break;
      if (target.value) {
        __privateMethod$1(this, _resetInputValue, resetInputValue_fn).call(this, target);
      } else {
        await this.updateComplete;
        const previousInput = __privateMethod$1(this, _focusPreviousInput, focusPreviousInput_fn).call(this, target);
        __privateMethod$1(this, _resetInputValue, resetInputValue_fn).call(this, previousInput);
      }
      break;
  }
};
_handleInvalid = /* @__PURE__ */ new WeakSet();
handleInvalid_fn = function(event) {
  event.preventDefault();
  if (isInput(event.target)) {
    event.target.setAttribute("aria-invalid", "true");
  }
};
_handleInput = /* @__PURE__ */ new WeakSet();
handleInput_fn = function({ target }, index) {
  if (!isInput(target))
    return;
  if (target.value === "") {
    this.code[index] = "";
    __privateMethod$1(this, _resetInputValue, resetInputValue_fn).call(this, target);
    return;
  }
  if (!/[0-9]{1}/.test(target.value)) {
    __privateMethod$1(this, _resetInputValue, resetInputValue_fn).call(this, target);
    return;
  }
  this.code[index] = target.value;
  target.setAttribute("aria-invalid", "false");
  const detail = {
    input: target,
    value: target.value
  };
  const changeEvent = new CustomEvent("olo-change", {
    detail,
    bubbles: true,
    cancelable: true
  });
  this.dispatchEvent(changeEvent);
  if (this.code.every(Boolean) && !__privateMethod$1(this, _getSubmitButton, getSubmitButton_fn).call(this)) {
    __privateMethod$1(this, _handleCodeComplete, handleCodeComplete_fn).call(this);
  } else {
    __privateMethod$1(this, _focusNextInput, focusNextInput_fn).call(this, target);
  }
};
_handleCodeComplete = /* @__PURE__ */ new WeakSet();
handleCodeComplete_fn = async function() {
  var _a;
  if (!__privateMethod$1(this, _getSubmitButton, getSubmitButton_fn).call(this)) {
    await this.updateComplete;
    (_a = this.form) == null ? void 0 : _a.requestSubmit();
  }
};
_handleSubmit = /* @__PURE__ */ new WeakSet();
handleSubmit_fn = async function(event) {
  var _a;
  event.preventDefault();
  const joined = this.code.join("");
  try {
    this.disableInputs();
    await ((_a = this.onCodeVerification) == null ? void 0 : _a.call(this, joined));
  } finally {
    this.enableInputs();
    this.reset();
  }
};
_handlePaste = /* @__PURE__ */ new WeakSet();
handlePaste_fn = async function(event) {
  var _a, _b, _c, _d;
  event.preventDefault();
  (_a = this.form) == null ? void 0 : _a.reset();
  const pasted = (_d = (_c = (_b = event.clipboardData) == null ? void 0 : _b.getData("text")) == null ? void 0 : _c.trim().slice(0, this.codeLength).split("").filter((d) => Number.isInteger(+d)).map((digit, index) => this.code[index] = digit)) != null ? _d : [];
  this.requestUpdate();
  __privateMethod$1(this, _focusEmptyInput, focusEmptyInput_fn).call(this);
  if (pasted.length === this.codeLength) {
    await this.updateComplete;
    __privateMethod$1(this, _handleCodeComplete, handleCodeComplete_fn).call(this);
  }
};
OloCodeVerification.styles = styles$3;
__decorateClass$3([
  query("form")
], OloCodeVerification.prototype, "form", 2);
__decorateClass$3([
  query("slot:not([name])")
], OloCodeVerification.prototype, "submitter", 2);
__decorateClass$3([
  property({ type: Number, attribute: "code-length" })
], OloCodeVerification.prototype, "codeLength", 2);
__decorateClass$3([
  property()
], OloCodeVerification.prototype, "code", 2);
OloCodeVerification = __decorateClass$3([
  customElement("olo-code-verification")
], OloCodeVerification);
var CodeMethod = /* @__PURE__ */ ((CodeMethod2) => {
  CodeMethod2["Email"] = "email";
  CodeMethod2["Text"] = "sms";
  return CodeMethod2;
})(CodeMethod || {});
const _OloAuthResponse = class {
  constructor(response) {
    this.index = _OloAuthResponse.index;
    this.rateLimited = false;
    this._json = null;
    this._text = "";
    this._arrayBuffer = null;
    this._blob = null;
    _OloAuthResponse.index += 1;
    this.response = response;
    this.rateLimited = response.status === 429;
  }
  get headers() {
    return this.response.headers;
  }
  get ok() {
    return this.response.ok;
  }
  get redirected() {
    return this.response.redirected;
  }
  get status() {
    return this.response.status;
  }
  get statusText() {
    return this.response.statusText;
  }
  get type() {
    return this.response.type;
  }
  get url() {
    return this.response.url;
  }
  get body() {
    return this.response.body;
  }
  get bodyUsed() {
    return this.response.bodyUsed;
  }
  clone() {
    return new _OloAuthResponse(this.response.clone());
  }
  async json() {
    return this._json || (this._json = await this.response.json());
  }
  async text() {
    return this._text || (this._text = await this.response.text());
  }
  async arrayBuffer() {
    return this._arrayBuffer || (this._arrayBuffer = await this.response.arrayBuffer());
  }
  async blob() {
    return this._blob || (this._blob = await this.response.blob());
  }
  async formData() {
    throw new Error("Method not implemented.");
  }
  async parseBody() {
    return this;
  }
};
let OloAuthResponse = _OloAuthResponse;
OloAuthResponse.index = 0;
let defaultApiUrl = "/api";
let defaultApiCredentials = "same-origin";
let fetchMethod;
const setDefaultApiUrl = (url) => defaultApiUrl = url.replace(new RegExp("^\\/+|(?<!\\/)\\/+$"), "");
const setFetchMethod = (method) => fetchMethod = method;
const request = async (url, init = {}) => {
  url = url.includes("://") ? url : `${defaultApiUrl}/${url}`;
  const response = await (fetchMethod || fetch)(url, {
    credentials: defaultApiCredentials,
    ...init
  });
  return response;
};
const post = async (url, { headers = {}, ...init } = {}) => request(url, {
  ...init,
  method: "POST",
  headers: new Headers({ "content-type": "application/json", ...headers })
});
class FindUserResponse extends OloAuthResponse {
  constructor(response) {
    super(response);
    this.found = false;
    this.found = this.status === 200;
  }
  async parseBody() {
    if (this.status === 200) {
      const { lastFourPhone, loginHint } = await this.json();
      this.phoneNumber = lastFourPhone;
      this.loginHint = loginHint;
    }
    return this;
  }
}
const findUser = async ({ email }, init) => {
  const response = await post("user/check", {
    body: JSON.stringify({
      email
    }),
    ...init
  });
  return await new FindUserResponse(response).parseBody();
};
var commonjsGlobal = typeof globalThis !== "undefined" ? globalThis : typeof window !== "undefined" ? window : typeof global !== "undefined" ? global : typeof self !== "undefined" ? self : {};
var FUNC_ERROR_TEXT = "Expected a function";
var NAN = 0 / 0;
var symbolTag = "[object Symbol]";
var reTrim = /^\s+|\s+$/g;
var reIsBadHex = /^[-+]0x[0-9a-f]+$/i;
var reIsBinary = /^0b[01]+$/i;
var reIsOctal = /^0o[0-7]+$/i;
var freeParseInt = parseInt;
var freeGlobal = typeof commonjsGlobal == "object" && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;
var freeSelf = typeof self == "object" && self && self.Object === Object && self;
var root = freeGlobal || freeSelf || Function("return this")();
var objectProto = Object.prototype;
var objectToString = objectProto.toString;
var nativeMax = Math.max, nativeMin = Math.min;
var now = function() {
  return root.Date.now();
};
function debounce(func, wait, options) {
  var lastArgs, lastThis, maxWait, result, timerId, lastCallTime, lastInvokeTime = 0, leading = false, maxing = false, trailing = true;
  if (typeof func != "function") {
    throw new TypeError(FUNC_ERROR_TEXT);
  }
  wait = toNumber(wait) || 0;
  if (isObject(options)) {
    leading = !!options.leading;
    maxing = "maxWait" in options;
    maxWait = maxing ? nativeMax(toNumber(options.maxWait) || 0, wait) : maxWait;
    trailing = "trailing" in options ? !!options.trailing : trailing;
  }
  function invokeFunc(time) {
    var args = lastArgs, thisArg = lastThis;
    lastArgs = lastThis = void 0;
    lastInvokeTime = time;
    result = func.apply(thisArg, args);
    return result;
  }
  function leadingEdge(time) {
    lastInvokeTime = time;
    timerId = setTimeout(timerExpired, wait);
    return leading ? invokeFunc(time) : result;
  }
  function remainingWait(time) {
    var timeSinceLastCall = time - lastCallTime, timeSinceLastInvoke = time - lastInvokeTime, result2 = wait - timeSinceLastCall;
    return maxing ? nativeMin(result2, maxWait - timeSinceLastInvoke) : result2;
  }
  function shouldInvoke(time) {
    var timeSinceLastCall = time - lastCallTime, timeSinceLastInvoke = time - lastInvokeTime;
    return lastCallTime === void 0 || timeSinceLastCall >= wait || timeSinceLastCall < 0 || maxing && timeSinceLastInvoke >= maxWait;
  }
  function timerExpired() {
    var time = now();
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    timerId = setTimeout(timerExpired, remainingWait(time));
  }
  function trailingEdge(time) {
    timerId = void 0;
    if (trailing && lastArgs) {
      return invokeFunc(time);
    }
    lastArgs = lastThis = void 0;
    return result;
  }
  function cancel() {
    if (timerId !== void 0) {
      clearTimeout(timerId);
    }
    lastInvokeTime = 0;
    lastArgs = lastCallTime = lastThis = timerId = void 0;
  }
  function flush() {
    return timerId === void 0 ? result : trailingEdge(now());
  }
  function debounced() {
    var time = now(), isInvoking = shouldInvoke(time);
    lastArgs = arguments;
    lastThis = this;
    lastCallTime = time;
    if (isInvoking) {
      if (timerId === void 0) {
        return leadingEdge(lastCallTime);
      }
      if (maxing) {
        timerId = setTimeout(timerExpired, wait);
        return invokeFunc(lastCallTime);
      }
    }
    if (timerId === void 0) {
      timerId = setTimeout(timerExpired, wait);
    }
    return result;
  }
  debounced.cancel = cancel;
  debounced.flush = flush;
  return debounced;
}
function isObject(value) {
  var type = typeof value;
  return !!value && (type == "object" || type == "function");
}
function isObjectLike(value) {
  return !!value && typeof value == "object";
}
function isSymbol(value) {
  return typeof value == "symbol" || isObjectLike(value) && objectToString.call(value) == symbolTag;
}
function toNumber(value) {
  if (typeof value == "number") {
    return value;
  }
  if (isSymbol(value)) {
    return NAN;
  }
  if (isObject(value)) {
    var other = typeof value.valueOf == "function" ? value.valueOf() : value;
    value = isObject(other) ? other + "" : other;
  }
  if (typeof value != "string") {
    return value === 0 ? value : +value;
  }
  value = value.replace(reTrim, "");
  var isBinary = reIsBinary.test(value);
  return isBinary || reIsOctal.test(value) ? freeParseInt(value.slice(2), isBinary ? 2 : 8) : reIsBadHex.test(value) ? NAN : +value;
}
var lodash_debounce = debounce;
class VerifyError extends Error {
  constructor(message, { cause } = {}) {
    super(message);
    if (cause) {
      this.cause = cause;
    }
  }
}
class VerifyChannelIdError extends VerifyError {
}
class VerifyLoginProviderSlugError extends VerifyError {
}
class VerifyCheckUserError extends VerifyError {
}
class VerifyRequestCodeError extends VerifyError {
}
class VerifySubmitCodeError extends VerifyError {
}
class VerifyAttachError extends VerifyError {
}
class VerifyDetachError extends VerifyError {
}
class VerifyShowError extends VerifyError {
}
class VerifyHideError extends VerifyError {
}
class VerifyRenderError extends VerifyError {
}
class RequestStatus {
  constructor(fn, mapErrorDescription = null) {
    this.loading = false;
    this.done = false;
    this.error = null;
    this.success = null;
    this.response = null;
    this.status = null;
    this.fn = null;
    this.abortController = null;
    this.fn = fn;
    this.mapErrorDescription = mapErrorDescription;
  }
  async run(args, init, useTimeout = false) {
    this.reset();
    await this.handleRequest(args, init, useTimeout);
    return this;
  }
  async handleRequest(args, init, useTimeout = false) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j;
    try {
      (_a = this.abortController) == null ? void 0 : _a.abort();
      this.abortController = new AbortController();
      const response = await Promise.race([
        (_b = this.fn) == null ? void 0 : _b.call(this, args, {
          ...init,
          signal: this.abortController.signal
        }),
        new Promise((resolve) => {
          if (useTimeout) {
            this.timeoutId = window.setTimeout(() => {
              var _a2;
              (_a2 = this.abortController) == null ? void 0 : _a2.abort();
              resolve("request_timeout");
            }, 5e3);
          }
        })
      ]);
      const requestTimedOut = response === "request_timeout";
      if (requestTimedOut) {
        this.error = Error("Request timed out");
        this.response = null;
        this.status = null;
      } else {
        this.response = response;
        this.status = response.status;
      }
      this.abortController = null;
      this.success = !!((_c = this.response) == null ? void 0 : _c.ok);
      if (!this.success && !requestTimedOut) {
        let body;
        try {
          body = await ((_d = this.response) == null ? void 0 : _d.json());
        } catch {
        }
        this.error = new Error(
          (_j = (_g = body == null ? void 0 : body.message) != null ? _g : (_f = this.mapErrorDescription) == null ? void 0 : _f.call(this, body == null ? void 0 : body.error_description, (_e = this.response) == null ? void 0 : _e.status)) != null ? _j : `Server error: ${((_h = this.response) == null ? void 0 : _h.statusText) || `Code ${(_i = this.response) == null ? void 0 : _i.status}`}`
        );
      }
    } catch (e) {
      if (!(e instanceof DOMException && e.name === "AbortError")) {
        this.error = e;
      }
    } finally {
      this.finish();
    }
  }
  reset() {
    this.done = false;
    this.loading = true;
    this.success = null;
    this.response = null;
    this.error = null;
    this.timeoutId = void 0;
  }
  finish() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    this.loading = false;
    this.done = true;
  }
}
class RequestStatusController extends RequestStatus {
  constructor(host, fn, mapErrorDescription = null) {
    super(fn, mapErrorDescription);
    this.host = host;
    this.host.addController(this);
  }
  hostConnected() {
  }
  hostDisconnected() {
  }
  async run(args, init, useTimeout = false) {
    this.reset();
    this.host.requestUpdate();
    await this.handleRequest(args, init, useTimeout);
    this.host.requestUpdate();
    return this;
  }
}
class SendCodeResponse extends OloAuthResponse {
}
const sendCode = async (req, init) => {
  if (!req.channelId) {
    delete req.channelId;
  }
  const response = await post("code", {
    method: "POST",
    body: JSON.stringify(req),
    headers: {
      Accept: "application/json"
    },
    ...init
  });
  return new SendCodeResponse(response);
};
class OTPLoginResponse extends OloAuthResponse {
  constructor(response) {
    super(response);
    this.verified = false;
    this.verified = this.status === 204;
  }
}
const otpLogin = async ({ provider, email, passcode, url, verificationMethod }, init) => {
  const response = await post(url, {
    method: "POST",
    body: JSON.stringify({ provider, email, passcode, verificationMethod }),
    ...init
  });
  return await new OTPLoginResponse(response).parseBody();
};
function getSide(placement) {
  return placement.split("-")[0];
}
function getAlignment(placement) {
  return placement.split("-")[1];
}
function getMainAxisFromPlacement(placement) {
  return ["top", "bottom"].includes(getSide(placement)) ? "x" : "y";
}
function getLengthFromAxis(axis) {
  return axis === "y" ? "height" : "width";
}
function computeCoordsFromPlacement(_ref, placement, rtl) {
  let {
    reference,
    floating
  } = _ref;
  const commonX = reference.x + reference.width / 2 - floating.width / 2;
  const commonY = reference.y + reference.height / 2 - floating.height / 2;
  const mainAxis = getMainAxisFromPlacement(placement);
  const length = getLengthFromAxis(mainAxis);
  const commonAlign = reference[length] / 2 - floating[length] / 2;
  const side = getSide(placement);
  const isVertical = mainAxis === "x";
  let coords;
  switch (side) {
    case "top":
      coords = {
        x: commonX,
        y: reference.y - floating.height
      };
      break;
    case "bottom":
      coords = {
        x: commonX,
        y: reference.y + reference.height
      };
      break;
    case "right":
      coords = {
        x: reference.x + reference.width,
        y: commonY
      };
      break;
    case "left":
      coords = {
        x: reference.x - floating.width,
        y: commonY
      };
      break;
    default:
      coords = {
        x: reference.x,
        y: reference.y
      };
  }
  switch (getAlignment(placement)) {
    case "start":
      coords[mainAxis] -= commonAlign * (rtl && isVertical ? -1 : 1);
      break;
    case "end":
      coords[mainAxis] += commonAlign * (rtl && isVertical ? -1 : 1);
      break;
  }
  return coords;
}
const computePosition$1 = async (reference, floating, config) => {
  const {
    placement = "bottom",
    strategy = "absolute",
    middleware = [],
    platform: platform2
  } = config;
  const rtl = await (platform2.isRTL == null ? void 0 : platform2.isRTL(floating));
  if (process.env.NODE_ENV !== "production") {
    if (platform2 == null) {
      console.error(["Floating UI: `platform` property was not passed to config. If you", "want to use Floating UI on the web, install @floating-ui/dom", "instead of the /core package. Otherwise, you can create your own", "`platform`: https://floating-ui.com/docs/platform"].join(" "));
    }
    if (middleware.filter((_ref) => {
      let {
        name
      } = _ref;
      return name === "autoPlacement" || name === "flip";
    }).length > 1) {
      throw new Error(["Floating UI: duplicate `flip` and/or `autoPlacement`", "middleware detected. This will lead to an infinite loop. Ensure only", "one of either has been passed to the `middleware` array."].join(" "));
    }
  }
  let rects = await platform2.getElementRects({
    reference,
    floating,
    strategy
  });
  let {
    x,
    y
  } = computeCoordsFromPlacement(rects, placement, rtl);
  let statefulPlacement = placement;
  let middlewareData = {};
  let resetCount = 0;
  for (let i = 0; i < middleware.length; i++) {
    const {
      name,
      fn
    } = middleware[i];
    const {
      x: nextX,
      y: nextY,
      data,
      reset
    } = await fn({
      x,
      y,
      initialPlacement: placement,
      placement: statefulPlacement,
      strategy,
      middlewareData,
      rects,
      platform: platform2,
      elements: {
        reference,
        floating
      }
    });
    x = nextX != null ? nextX : x;
    y = nextY != null ? nextY : y;
    middlewareData = {
      ...middlewareData,
      [name]: {
        ...middlewareData[name],
        ...data
      }
    };
    if (process.env.NODE_ENV !== "production") {
      if (resetCount > 50) {
        console.warn(["Floating UI: The middleware lifecycle appears to be running in an", "infinite loop. This is usually caused by a `reset` continually", "being returned without a break condition."].join(" "));
      }
    }
    if (reset && resetCount <= 50) {
      resetCount++;
      if (typeof reset === "object") {
        if (reset.placement) {
          statefulPlacement = reset.placement;
        }
        if (reset.rects) {
          rects = reset.rects === true ? await platform2.getElementRects({
            reference,
            floating,
            strategy
          }) : reset.rects;
        }
        ({
          x,
          y
        } = computeCoordsFromPlacement(rects, statefulPlacement, rtl));
      }
      i = -1;
      continue;
    }
  }
  return {
    x,
    y,
    placement: statefulPlacement,
    strategy,
    middlewareData
  };
};
function expandPaddingObject(padding) {
  return {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    ...padding
  };
}
function getSideObjectFromPadding(padding) {
  return typeof padding !== "number" ? expandPaddingObject(padding) : {
    top: padding,
    right: padding,
    bottom: padding,
    left: padding
  };
}
function rectToClientRect(rect) {
  return {
    ...rect,
    top: rect.y,
    left: rect.x,
    right: rect.x + rect.width,
    bottom: rect.y + rect.height
  };
}
const min$1 = Math.min;
const max$1 = Math.max;
function within(min$1$1, value, max$1$1) {
  return max$1(min$1$1, min$1(value, max$1$1));
}
const arrow = (options) => ({
  name: "arrow",
  options,
  async fn(middlewareArguments) {
    const {
      element,
      padding = 0
    } = options != null ? options : {};
    const {
      x,
      y,
      placement,
      rects,
      platform: platform2
    } = middlewareArguments;
    if (element == null) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("Floating UI: No `element` was passed to the `arrow` middleware.");
      }
      return {};
    }
    const paddingObject = getSideObjectFromPadding(padding);
    const coords = {
      x,
      y
    };
    const axis = getMainAxisFromPlacement(placement);
    const alignment = getAlignment(placement);
    const length = getLengthFromAxis(axis);
    const arrowDimensions = await platform2.getDimensions(element);
    const minProp = axis === "y" ? "top" : "left";
    const maxProp = axis === "y" ? "bottom" : "right";
    const endDiff = rects.reference[length] + rects.reference[axis] - coords[axis] - rects.floating[length];
    const startDiff = coords[axis] - rects.reference[axis];
    const arrowOffsetParent = await (platform2.getOffsetParent == null ? void 0 : platform2.getOffsetParent(element));
    let clientSize = arrowOffsetParent ? axis === "y" ? arrowOffsetParent.clientHeight || 0 : arrowOffsetParent.clientWidth || 0 : 0;
    if (clientSize === 0) {
      clientSize = rects.floating[length];
    }
    const centerToReference = endDiff / 2 - startDiff / 2;
    const min2 = paddingObject[minProp];
    const max2 = clientSize - arrowDimensions[length] - paddingObject[maxProp];
    const center = clientSize / 2 - arrowDimensions[length] / 2 + centerToReference;
    const offset2 = within(min2, center, max2);
    const alignmentPadding = alignment === "start" ? paddingObject[minProp] : paddingObject[maxProp];
    const shouldAddOffset = alignmentPadding > 0 && center !== offset2 && rects.reference[length] <= rects.floating[length];
    const alignmentOffset = shouldAddOffset ? center < min2 ? min2 - center : max2 - center : 0;
    return {
      [axis]: coords[axis] - alignmentOffset,
      data: {
        [axis]: offset2,
        centerOffset: center - offset2
      }
    };
  }
});
async function convertValueToCoords(middlewareArguments, value) {
  const {
    placement,
    platform: platform2,
    elements
  } = middlewareArguments;
  const rtl = await (platform2.isRTL == null ? void 0 : platform2.isRTL(elements.floating));
  const side = getSide(placement);
  const alignment = getAlignment(placement);
  const isVertical = getMainAxisFromPlacement(placement) === "x";
  const mainAxisMulti = ["left", "top"].includes(side) ? -1 : 1;
  const crossAxisMulti = rtl && isVertical ? -1 : 1;
  const rawValue = typeof value === "function" ? value(middlewareArguments) : value;
  let {
    mainAxis,
    crossAxis,
    alignmentAxis
  } = typeof rawValue === "number" ? {
    mainAxis: rawValue,
    crossAxis: 0,
    alignmentAxis: null
  } : {
    mainAxis: 0,
    crossAxis: 0,
    alignmentAxis: null,
    ...rawValue
  };
  if (alignment && typeof alignmentAxis === "number") {
    crossAxis = alignment === "end" ? alignmentAxis * -1 : alignmentAxis;
  }
  return isVertical ? {
    x: crossAxis * crossAxisMulti,
    y: mainAxis * mainAxisMulti
  } : {
    x: mainAxis * mainAxisMulti,
    y: crossAxis * crossAxisMulti
  };
}
const offset = function(value) {
  if (value === void 0) {
    value = 0;
  }
  return {
    name: "offset",
    options: value,
    async fn(middlewareArguments) {
      const {
        x,
        y
      } = middlewareArguments;
      const diffCoords = await convertValueToCoords(middlewareArguments, value);
      return {
        x: x + diffCoords.x,
        y: y + diffCoords.y,
        data: diffCoords
      };
    }
  };
};
function isWindow(value) {
  return value && value.document && value.location && value.alert && value.setInterval;
}
function getWindow(node) {
  if (node == null) {
    return window;
  }
  if (!isWindow(node)) {
    const ownerDocument = node.ownerDocument;
    return ownerDocument ? ownerDocument.defaultView || window : window;
  }
  return node;
}
function getComputedStyle(element) {
  return getWindow(element).getComputedStyle(element);
}
function getNodeName(node) {
  return isWindow(node) ? "" : node ? (node.nodeName || "").toLowerCase() : "";
}
function getUAString() {
  const uaData = navigator.userAgentData;
  if (uaData != null && uaData.brands) {
    return uaData.brands.map((item) => item.brand + "/" + item.version).join(" ");
  }
  return navigator.userAgent;
}
function isHTMLElement(value) {
  return value instanceof getWindow(value).HTMLElement;
}
function isElement(value) {
  return value instanceof getWindow(value).Element;
}
function isNode(value) {
  return value instanceof getWindow(value).Node;
}
function isShadowRoot(node) {
  if (typeof ShadowRoot === "undefined") {
    return false;
  }
  const OwnElement = getWindow(node).ShadowRoot;
  return node instanceof OwnElement || node instanceof ShadowRoot;
}
function isOverflowElement(element) {
  const {
    overflow,
    overflowX,
    overflowY
  } = getComputedStyle(element);
  return /auto|scroll|overlay|hidden/.test(overflow + overflowY + overflowX);
}
function isTableElement(element) {
  return ["table", "td", "th"].includes(getNodeName(element));
}
function isContainingBlock(element) {
  const isFirefox = /firefox/i.test(getUAString());
  const css2 = getComputedStyle(element);
  return css2.transform !== "none" || css2.perspective !== "none" || css2.contain === "paint" || ["transform", "perspective"].includes(css2.willChange) || isFirefox && css2.willChange === "filter" || isFirefox && (css2.filter ? css2.filter !== "none" : false);
}
function isLayoutViewport() {
  return !/^((?!chrome|android).)*safari/i.test(getUAString());
}
const min = Math.min;
const max = Math.max;
const round = Math.round;
function getBoundingClientRect(element, includeScale, isFixedStrategy) {
  var _win$visualViewport$o, _win$visualViewport, _win$visualViewport$o2, _win$visualViewport2;
  if (includeScale === void 0) {
    includeScale = false;
  }
  if (isFixedStrategy === void 0) {
    isFixedStrategy = false;
  }
  const clientRect = element.getBoundingClientRect();
  let scaleX = 1;
  let scaleY = 1;
  if (includeScale && isHTMLElement(element)) {
    scaleX = element.offsetWidth > 0 ? round(clientRect.width) / element.offsetWidth || 1 : 1;
    scaleY = element.offsetHeight > 0 ? round(clientRect.height) / element.offsetHeight || 1 : 1;
  }
  const win = isElement(element) ? getWindow(element) : window;
  const addVisualOffsets = !isLayoutViewport() && isFixedStrategy;
  const x = (clientRect.left + (addVisualOffsets ? (_win$visualViewport$o = (_win$visualViewport = win.visualViewport) == null ? void 0 : _win$visualViewport.offsetLeft) != null ? _win$visualViewport$o : 0 : 0)) / scaleX;
  const y = (clientRect.top + (addVisualOffsets ? (_win$visualViewport$o2 = (_win$visualViewport2 = win.visualViewport) == null ? void 0 : _win$visualViewport2.offsetTop) != null ? _win$visualViewport$o2 : 0 : 0)) / scaleY;
  const width = clientRect.width / scaleX;
  const height = clientRect.height / scaleY;
  return {
    width,
    height,
    top: y,
    right: x + width,
    bottom: y + height,
    left: x,
    x,
    y
  };
}
function getDocumentElement(node) {
  return ((isNode(node) ? node.ownerDocument : node.document) || window.document).documentElement;
}
function getNodeScroll(element) {
  if (isElement(element)) {
    return {
      scrollLeft: element.scrollLeft,
      scrollTop: element.scrollTop
    };
  }
  return {
    scrollLeft: element.pageXOffset,
    scrollTop: element.pageYOffset
  };
}
function getWindowScrollBarX(element) {
  return getBoundingClientRect(getDocumentElement(element)).left + getNodeScroll(element).scrollLeft;
}
function isScaled(element) {
  const rect = getBoundingClientRect(element);
  return round(rect.width) !== element.offsetWidth || round(rect.height) !== element.offsetHeight;
}
function getRectRelativeToOffsetParent(element, offsetParent, strategy) {
  const isOffsetParentAnElement = isHTMLElement(offsetParent);
  const documentElement = getDocumentElement(offsetParent);
  const rect = getBoundingClientRect(
    element,
    isOffsetParentAnElement && isScaled(offsetParent),
    strategy === "fixed"
  );
  let scroll = {
    scrollLeft: 0,
    scrollTop: 0
  };
  const offsets = {
    x: 0,
    y: 0
  };
  if (isOffsetParentAnElement || !isOffsetParentAnElement && strategy !== "fixed") {
    if (getNodeName(offsetParent) !== "body" || isOverflowElement(documentElement)) {
      scroll = getNodeScroll(offsetParent);
    }
    if (isHTMLElement(offsetParent)) {
      const offsetRect = getBoundingClientRect(offsetParent, true);
      offsets.x = offsetRect.x + offsetParent.clientLeft;
      offsets.y = offsetRect.y + offsetParent.clientTop;
    } else if (documentElement) {
      offsets.x = getWindowScrollBarX(documentElement);
    }
  }
  return {
    x: rect.left + scroll.scrollLeft - offsets.x,
    y: rect.top + scroll.scrollTop - offsets.y,
    width: rect.width,
    height: rect.height
  };
}
function getParentNode(node) {
  if (getNodeName(node) === "html") {
    return node;
  }
  return node.assignedSlot || node.parentNode || (isShadowRoot(node) ? node.host : null) || getDocumentElement(node);
}
function getTrueOffsetParent(element) {
  if (!isHTMLElement(element) || getComputedStyle(element).position === "fixed") {
    return null;
  }
  return composedOffsetParent(element);
}
function composedOffsetParent(element) {
  let {
    offsetParent
  } = element;
  let ancestor = element;
  let foundInsideSlot = false;
  while (ancestor && ancestor !== offsetParent) {
    const {
      assignedSlot
    } = ancestor;
    if (assignedSlot) {
      let newOffsetParent = assignedSlot.offsetParent;
      if (getComputedStyle(assignedSlot).display === "contents") {
        const hadStyleAttribute = assignedSlot.hasAttribute("style");
        const oldDisplay = assignedSlot.style.display;
        assignedSlot.style.display = getComputedStyle(ancestor).display;
        newOffsetParent = assignedSlot.offsetParent;
        assignedSlot.style.display = oldDisplay;
        if (!hadStyleAttribute) {
          assignedSlot.removeAttribute("style");
        }
      }
      ancestor = assignedSlot;
      if (offsetParent !== newOffsetParent) {
        offsetParent = newOffsetParent;
        foundInsideSlot = true;
      }
    } else if (isShadowRoot(ancestor) && ancestor.host && foundInsideSlot) {
      break;
    }
    ancestor = isShadowRoot(ancestor) && ancestor.host || ancestor.parentNode;
  }
  return offsetParent;
}
function getContainingBlock(element) {
  let currentNode = getParentNode(element);
  if (isShadowRoot(currentNode)) {
    currentNode = currentNode.host;
  }
  while (isHTMLElement(currentNode) && !["html", "body"].includes(getNodeName(currentNode))) {
    if (isContainingBlock(currentNode)) {
      return currentNode;
    } else {
      const parent = currentNode.parentNode;
      currentNode = isShadowRoot(parent) ? parent.host : parent;
    }
  }
  return null;
}
function getOffsetParent(element) {
  const window2 = getWindow(element);
  let offsetParent = getTrueOffsetParent(element);
  while (offsetParent && isTableElement(offsetParent) && getComputedStyle(offsetParent).position === "static") {
    offsetParent = getTrueOffsetParent(offsetParent);
  }
  if (offsetParent && (getNodeName(offsetParent) === "html" || getNodeName(offsetParent) === "body" && getComputedStyle(offsetParent).position === "static" && !isContainingBlock(offsetParent))) {
    return window2;
  }
  return offsetParent || getContainingBlock(element) || window2;
}
function getDimensions(element) {
  if (isHTMLElement(element)) {
    return {
      width: element.offsetWidth,
      height: element.offsetHeight
    };
  }
  const rect = getBoundingClientRect(element);
  return {
    width: rect.width,
    height: rect.height
  };
}
function convertOffsetParentRelativeRectToViewportRelativeRect(_ref) {
  let {
    rect,
    offsetParent,
    strategy
  } = _ref;
  const isOffsetParentAnElement = isHTMLElement(offsetParent);
  const documentElement = getDocumentElement(offsetParent);
  if (offsetParent === documentElement) {
    return rect;
  }
  let scroll = {
    scrollLeft: 0,
    scrollTop: 0
  };
  const offsets = {
    x: 0,
    y: 0
  };
  if (isOffsetParentAnElement || !isOffsetParentAnElement && strategy !== "fixed") {
    if (getNodeName(offsetParent) !== "body" || isOverflowElement(documentElement)) {
      scroll = getNodeScroll(offsetParent);
    }
    if (isHTMLElement(offsetParent)) {
      const offsetRect = getBoundingClientRect(offsetParent, true);
      offsets.x = offsetRect.x + offsetParent.clientLeft;
      offsets.y = offsetRect.y + offsetParent.clientTop;
    }
  }
  return {
    ...rect,
    x: rect.x - scroll.scrollLeft + offsets.x,
    y: rect.y - scroll.scrollTop + offsets.y
  };
}
function getViewportRect(element, strategy) {
  const win = getWindow(element);
  const html2 = getDocumentElement(element);
  const visualViewport = win.visualViewport;
  let width = html2.clientWidth;
  let height = html2.clientHeight;
  let x = 0;
  let y = 0;
  if (visualViewport) {
    width = visualViewport.width;
    height = visualViewport.height;
    const layoutViewport = isLayoutViewport();
    if (layoutViewport || !layoutViewport && strategy === "fixed") {
      x = visualViewport.offsetLeft;
      y = visualViewport.offsetTop;
    }
  }
  return {
    width,
    height,
    x,
    y
  };
}
function getDocumentRect(element) {
  var _element$ownerDocumen;
  const html2 = getDocumentElement(element);
  const scroll = getNodeScroll(element);
  const body = (_element$ownerDocumen = element.ownerDocument) == null ? void 0 : _element$ownerDocumen.body;
  const width = max(html2.scrollWidth, html2.clientWidth, body ? body.scrollWidth : 0, body ? body.clientWidth : 0);
  const height = max(html2.scrollHeight, html2.clientHeight, body ? body.scrollHeight : 0, body ? body.clientHeight : 0);
  let x = -scroll.scrollLeft + getWindowScrollBarX(element);
  const y = -scroll.scrollTop;
  if (getComputedStyle(body || html2).direction === "rtl") {
    x += max(html2.clientWidth, body ? body.clientWidth : 0) - width;
  }
  return {
    width,
    height,
    x,
    y
  };
}
function getNearestOverflowAncestor(node) {
  const parentNode = getParentNode(node);
  if (["html", "body", "#document"].includes(getNodeName(parentNode))) {
    return node.ownerDocument.body;
  }
  if (isHTMLElement(parentNode) && isOverflowElement(parentNode)) {
    return parentNode;
  }
  return getNearestOverflowAncestor(parentNode);
}
function getOverflowAncestors(node, list) {
  var _node$ownerDocument;
  if (list === void 0) {
    list = [];
  }
  const scrollableAncestor = getNearestOverflowAncestor(node);
  const isBody = scrollableAncestor === ((_node$ownerDocument = node.ownerDocument) == null ? void 0 : _node$ownerDocument.body);
  const win = getWindow(scrollableAncestor);
  const target = isBody ? [win].concat(win.visualViewport || [], isOverflowElement(scrollableAncestor) ? scrollableAncestor : []) : scrollableAncestor;
  const updatedList = list.concat(target);
  return isBody ? updatedList : updatedList.concat(getOverflowAncestors(target));
}
function contains(parent, child) {
  const rootNode = child.getRootNode == null ? void 0 : child.getRootNode();
  if (parent.contains(child)) {
    return true;
  } else if (rootNode && isShadowRoot(rootNode)) {
    let next = child;
    do {
      if (next && parent === next) {
        return true;
      }
      next = next.parentNode || next.host;
    } while (next);
  }
  return false;
}
function getInnerBoundingClientRect(element, strategy) {
  const clientRect = getBoundingClientRect(element, false, strategy === "fixed");
  const top = clientRect.top + element.clientTop;
  const left = clientRect.left + element.clientLeft;
  return {
    top,
    left,
    x: left,
    y: top,
    right: left + element.clientWidth,
    bottom: top + element.clientHeight,
    width: element.clientWidth,
    height: element.clientHeight
  };
}
function getClientRectFromClippingAncestor(element, clippingParent, strategy) {
  if (clippingParent === "viewport") {
    return rectToClientRect(getViewportRect(element, strategy));
  }
  if (isElement(clippingParent)) {
    return getInnerBoundingClientRect(clippingParent, strategy);
  }
  return rectToClientRect(getDocumentRect(getDocumentElement(element)));
}
function getClippingAncestors(element) {
  const clippingAncestors = getOverflowAncestors(element);
  const canEscapeClipping = ["absolute", "fixed"].includes(getComputedStyle(element).position);
  const clipperElement = canEscapeClipping && isHTMLElement(element) ? getOffsetParent(element) : element;
  if (!isElement(clipperElement)) {
    return [];
  }
  return clippingAncestors.filter((clippingAncestors2) => isElement(clippingAncestors2) && contains(clippingAncestors2, clipperElement) && getNodeName(clippingAncestors2) !== "body");
}
function getClippingRect(_ref) {
  let {
    element,
    boundary,
    rootBoundary,
    strategy
  } = _ref;
  const mainClippingAncestors = boundary === "clippingAncestors" ? getClippingAncestors(element) : [].concat(boundary);
  const clippingAncestors = [...mainClippingAncestors, rootBoundary];
  const firstClippingAncestor = clippingAncestors[0];
  const clippingRect = clippingAncestors.reduce((accRect, clippingAncestor) => {
    const rect = getClientRectFromClippingAncestor(element, clippingAncestor, strategy);
    accRect.top = max(rect.top, accRect.top);
    accRect.right = min(rect.right, accRect.right);
    accRect.bottom = min(rect.bottom, accRect.bottom);
    accRect.left = max(rect.left, accRect.left);
    return accRect;
  }, getClientRectFromClippingAncestor(element, firstClippingAncestor, strategy));
  return {
    width: clippingRect.right - clippingRect.left,
    height: clippingRect.bottom - clippingRect.top,
    x: clippingRect.left,
    y: clippingRect.top
  };
}
const platform = {
  getClippingRect,
  convertOffsetParentRelativeRectToViewportRelativeRect,
  isElement,
  getDimensions,
  getOffsetParent,
  getDocumentElement,
  getElementRects: (_ref) => {
    let {
      reference,
      floating,
      strategy
    } = _ref;
    return {
      reference: getRectRelativeToOffsetParent(reference, getOffsetParent(floating), strategy),
      floating: {
        ...getDimensions(floating),
        x: 0,
        y: 0
      }
    };
  },
  getClientRects: (element) => Array.from(element.getClientRects()),
  isRTL: (element) => getComputedStyle(element).direction === "rtl"
};
function autoUpdate(reference, floating, update, options) {
  if (options === void 0) {
    options = {};
  }
  const {
    ancestorScroll: _ancestorScroll = true,
    ancestorResize: _ancestorResize = true,
    elementResize = true,
    animationFrame = false
  } = options;
  const ancestorScroll = _ancestorScroll && !animationFrame;
  const ancestorResize = _ancestorResize && !animationFrame;
  const ancestors = ancestorScroll || ancestorResize ? [...isElement(reference) ? getOverflowAncestors(reference) : [], ...getOverflowAncestors(floating)] : [];
  ancestors.forEach((ancestor) => {
    ancestorScroll && ancestor.addEventListener("scroll", update, {
      passive: true
    });
    ancestorResize && ancestor.addEventListener("resize", update);
  });
  let observer = null;
  if (elementResize) {
    let initialUpdate = true;
    observer = new ResizeObserver(() => {
      if (!initialUpdate) {
        update();
      }
      initialUpdate = false;
    });
    isElement(reference) && !animationFrame && observer.observe(reference);
    observer.observe(floating);
  }
  let frameId;
  let prevRefRect = animationFrame ? getBoundingClientRect(reference) : null;
  if (animationFrame) {
    frameLoop();
  }
  function frameLoop() {
    const nextRefRect = getBoundingClientRect(reference);
    if (prevRefRect && (nextRefRect.x !== prevRefRect.x || nextRefRect.y !== prevRefRect.y || nextRefRect.width !== prevRefRect.width || nextRefRect.height !== prevRefRect.height)) {
      update();
    }
    prevRefRect = nextRefRect;
    frameId = requestAnimationFrame(frameLoop);
  }
  update();
  return () => {
    var _observer;
    ancestors.forEach((ancestor) => {
      ancestorScroll && ancestor.removeEventListener("scroll", update);
      ancestorResize && ancestor.removeEventListener("resize", update);
    });
    (_observer = observer) == null ? void 0 : _observer.disconnect();
    observer = null;
    if (animationFrame) {
      cancelAnimationFrame(frameId);
    }
  };
}
const computePosition = (reference, floating, options) => computePosition$1(reference, floating, {
  platform,
  ...options
});
const formatPhoneNumber = (value, regex = /[^0-9]/g) => {
  value = value.replace(regex, "");
  const areaCode = value.slice(0, 3);
  const firstThree = value.slice(3, 6);
  const lastFour = value.slice(6, 10);
  if (!value)
    return "";
  if (areaCode.length < 3 || !firstThree)
    return `(${areaCode}`;
  if (firstThree.length < 3 || !lastFour)
    return `(${areaCode}) ${firstThree}`;
  return `(${areaCode}) ${firstThree}-${lastFour}`;
};
const maskPhoneNumber = (value) => formatPhoneNumber(`••••••${value.slice(-4)}`, /[^0-9•]/g);
const logo$1 = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTU5IiBoZWlnaHQ9IjMxIiB2aWV3Qm94PSIwIDAgMTU5IDMxIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cGF0aCBkPSJNMTE5LjEwNSA1LjA4ODg3QzExMy41MjMgNS4wODg4NyAxMDkgOS42MjQ5OCAxMDkgMTUuMTg4NEMxMDkgMjAuNzUxOSAxMTMuNTIzIDI1LjI4OCAxMTkuMTA1IDI1LjI4OEMxMjQuNjg3IDI1LjI4OCAxMjkuMjEgMjAuNzY3NyAxMjkuMjEgMTUuMTg4NEMxMjkuMjEgOS42MDkxOCAxMjQuNjg3IDUuMDg4ODcgMTE5LjEwNSA1LjA4ODg3Wk0xMTkuMTA1IDIwLjUzMDZDMTE2LjE2NCAyMC41MzA2IDExMy43NzYgMTguMTQ0IDExMy43NzYgMTUuMTg4NEMxMTMuNzc2IDEyLjI0ODcgMTE2LjE2NCA5Ljg2MjA2IDExOS4xMDUgOS44NjIwNkMxMjIuMDQ2IDkuODYyMDYgMTI0LjQzNCAxMi4yNDg3IDEyNC40MzQgMTUuMTg4NEMxMjQuNDUgMTguMTQ0IDEyMi4wNjIgMjAuNTMwNiAxMTkuMTA1IDIwLjUzMDZaIiBmaWxsPSIjMkQ5NkZGIi8+CjxwYXRoIGQ9Ik0xNDguNjUxIDUuMDg4OTNDMTQzLjA2OSA1LjA4ODkzIDEzOC41NDcgOS42MDkyNCAxMzguNTQ3IDE1LjE4ODVDMTM4LjU0NyAyMC43Njc4IDE0My4wNjkgMjUuMjg4MSAxNDguNjUxIDI1LjI4ODFDMTU0LjIzNCAyNS4yODgxIDE1OC43NTYgMjAuNzY3OCAxNTguNzU2IDE1LjE4ODVDMTU4Ljc1NiA5LjYwOTI0IDE1NC4yMzQgNS4wODg5MyAxNDguNjUxIDUuMDg4OTNaTTE0OC42NTEgMjAuNTMwN0MxNDUuNzEgMjAuNTMwNyAxNDMuMzIyIDE4LjE0NDEgMTQzLjMyMiAxNS4xODg1QzE0My4zMjIgMTIuMjQ4NyAxNDUuNzEgOS44NjIxMiAxNDguNjUxIDkuODYyMTJDMTUxLjU5MyA5Ljg2MjEyIDE1My45ODEgMTIuMjQ4NyAxNTMuOTgxIDE1LjE4ODVDMTUzLjk4MSAxOC4xNDQxIDE1MS41OTMgMjAuNTMwNyAxNDguNjUxIDIwLjUzMDdaIiBmaWxsPSIjMkQ5NkZGIi8+CjxwYXRoIGQ9Ik0xMzYuMjY2IDBIMTMxLjQ5MVYzMC40MDk0SDEzNi4yNjZWMFoiIGZpbGw9IiMyRDk2RkYiLz4KPHBhdGggZD0iTTEuNDgwMzIgMjJWOC45MDkwOUg2LjE0NjUxQzcuMTY0OTggOC45MDkwOSA4LjAwODczIDkuMDk0NDYgOC42Nzc3NiA5LjQ2NTJDOS4zNDY3OSA5LjgzNTk0IDkuODQ3NSAxMC4zNDMgMTAuMTc5OSAxMC45ODY1QzEwLjUxMjMgMTEuNjI1NyAxMC42Nzg1IDEyLjM0NTkgMTAuNjc4NSAxMy4xNDdDMTAuNjc4NSAxMy45NTI0IDEwLjUxMDEgMTQuNjc2OCAxMC4xNzM1IDE1LjMyMDNDOS44NDExMSAxNS45NTk1IDkuMzM4MjcgMTYuNDY2NiA4LjY2NDk4IDE2Ljg0MTZDNy45OTU5NCAxNy4yMTI0IDcuMTU0MzIgMTcuMzk3NyA2LjE0MDEyIDE3LjM5NzdIMi45MzEzMVYxNS43MjNINS45NjExNEM2LjYwNDYxIDE1LjcyMyA3LjEyNjYyIDE1LjYxMjIgNy41MjcxOSAxNS4zOTA2QzcuOTI3NzYgMTUuMTY0OCA4LjIyMTc5IDE0Ljg1OCA4LjQwOTI5IDE0LjQ3MDJDOC41OTY3OSAxNC4wODI0IDguNjkwNTQgMTMuNjQxMyA4LjY5MDU0IDEzLjE0N0M4LjY5MDU0IDEyLjY1MjcgOC41OTY3OSAxMi4yMTM4IDguNDA5MjkgMTEuODMwM0M4LjIyMTc5IDExLjQ0NjcgNy45MjU2MyAxMS4xNDYzIDcuNTIwOCAxMC45MjlDNy4xMjAyMyAxMC43MTE2IDYuNTkxODIgMTAuNjAzIDUuOTM1NTcgMTAuNjAzSDMuNDU1NDZWMjJIMS40ODAzMlpNMTcuMDMzMiAyMi4xOTgyQzE2LjExMjcgMjIuMTk4MiAxNS4zMDk1IDIxLjk4NzIgMTQuNjIzNCAyMS41NjUzQzEzLjkzNzMgMjEuMTQzNSAxMy40MDQ2IDIwLjU1MzMgMTMuMDI1NCAxOS43OTQ3QzEyLjY0NjEgMTkuMDM2MiAxMi40NTY1IDE4LjE0OTkgMTIuNDU2NSAxNy4xMzU3QzEyLjQ1NjUgMTYuMTE3MiAxMi42NDYxIDE1LjIyNjYgMTMuMDI1NCAxNC40NjM4QzEzLjQwNDYgMTMuNzAxIDEzLjkzNzMgMTMuMTA4NyAxNC42MjM0IDEyLjY4NjhDMTUuMzA5NSAxMi4yNjQ5IDE2LjExMjcgMTIuMDU0IDE3LjAzMzIgMTIuMDU0QzE3Ljk1MzYgMTIuMDU0IDE4Ljc1NjkgMTIuMjY0OSAxOS40NDMgMTIuNjg2OEMyMC4xMjkxIDEzLjEwODcgMjAuNjYxNyAxMy43MDEgMjEuMDQxIDE0LjQ2MzhDMjEuNDIwMyAxNS4yMjY2IDIxLjYwOTkgMTYuMTE3MiAyMS42MDk5IDE3LjEzNTdDMjEuNjA5OSAxOC4xNDk5IDIxLjQyMDMgMTkuMDM2MiAyMS4wNDEgMTkuNzk0N0MyMC42NjE3IDIwLjU1MzMgMjAuMTI5MSAyMS4xNDM1IDE5LjQ0MyAyMS41NjUzQzE4Ljc1NjkgMjEuOTg3MiAxNy45NTM2IDIyLjE5ODIgMTcuMDMzMiAyMi4xOTgyWk0xNy4wMzk2IDIwLjU5MzhDMTcuNjM2MiAyMC41OTM4IDE4LjEzMDUgMjAuNDM2MSAxOC41MjI1IDIwLjEyMDdDMTguOTE0NiAxOS44MDU0IDE5LjIwNDQgMTkuMzg1NyAxOS4zOTE5IDE4Ljg2MTVDMTkuNTgzNiAxOC4zMzc0IDE5LjY3OTUgMTcuNzU5OSAxOS42Nzk1IDE3LjEyOTNDMTkuNjc5NSAxNi41MDI4IDE5LjU4MzYgMTUuOTI3NiAxOS4zOTE5IDE1LjQwMzRDMTkuMjA0NCAxNC44NzUgMTguOTE0NiAxNC40NTEgMTguNTIyNSAxNC4xMzE0QzE4LjEzMDUgMTMuODExOCAxNy42MzYyIDEzLjY1MiAxNy4wMzk2IDEzLjY1MkMxNi40Mzg3IDEzLjY1MiAxNS45NDAxIDEzLjgxMTggMTUuNTQzOCAxNC4xMzE0QzE1LjE1MTggMTQuNDUxIDE0Ljg1OTkgMTQuODc1IDE0LjY2ODEgMTUuNDAzNEMxNC40ODA2IDE1LjkyNzYgMTQuMzg2OSAxNi41MDI4IDE0LjM4NjkgMTcuMTI5M0MxNC4zODY5IDE3Ljc1OTkgMTQuNDgwNiAxOC4zMzc0IDE0LjY2ODEgMTguODYxNUMxNC44NTk5IDE5LjM4NTcgMTUuMTUxOCAxOS44MDU0IDE1LjU0MzggMjAuMTIwN0MxNS45NDAxIDIwLjQzNjEgMTYuNDM4NyAyMC41OTM4IDE3LjAzOTYgMjAuNTkzOFpNMjUuNzUzIDIyTDIyLjg2MzggMTIuMTgxOEgyNC44Mzg5TDI2Ljc2MjkgMTkuMzkySDI2Ljg1ODhMMjguNzg5MiAxMi4xODE4SDMwLjc2NDNMMzIuNjgxOSAxOS4zNjAxSDMyLjc3NzhMMzQuNjg5IDEyLjE4MThIMzYuNjY0MkwzMy43ODE0IDIySDMxLjgzMThMMjkuODM3NSAxNC45MTEySDI5LjY5MDVMMjcuNjk2MSAyMkgyNS43NTNaTTQyLjU5MDYgMjIuMTk4MkM0MS42MjMzIDIyLjE5ODIgNDAuNzkwMiAyMS45OTE1IDQwLjA5MTMgMjEuNTc4MUMzOS4zOTY3IDIxLjE2MDUgMzguODU5OCAyMC41NzQ2IDM4LjQ4MDUgMTkuODIwM0MzOC4xMDU1IDE5LjA2MTggMzcuOTE4IDE4LjE3MzMgMzcuOTE4IDE3LjE1NDhDMzcuOTE4IDE2LjE0OTEgMzguMTA1NSAxNS4yNjI4IDM4LjQ4MDUgMTQuNDk1N0MzOC44NTk4IDEzLjcyODcgMzkuMzg4MiAxMy4xMyA0MC4wNjU4IDEyLjY5OTZDNDAuNzQ3NiAxMi4yNjkyIDQxLjU0NDUgMTIuMDU0IDQyLjQ1NjQgMTIuMDU0QzQzLjAxMDQgMTIuMDU0IDQzLjU0NzMgMTIuMTQ1NiA0NC4wNjcyIDEyLjMyODhDNDQuNTg3MSAxMi41MTIxIDQ1LjA1MzcgMTIuNzk5NyA0NS40NjcxIDEzLjE5MThDNDUuODgwNCAxMy41ODM4IDQ2LjIwNjQgMTQuMDkzIDQ2LjQ0NSAxNC43MTk1QzQ2LjY4MzcgMTUuMzQxNiA0Ni44MDMgMTYuMDk4IDQ2LjgwMyAxNi45ODg2VjE3LjY2NjJIMzguOTk4M1YxNi4yMzQ0SDQ0LjkzMDFDNDQuOTMwMSAxNS43MzE1IDQ0LjgyNzggMTUuMjg2MiA0NC42MjMzIDE0Ljg5ODRDNDQuNDE4OCAxNC41MDY0IDQ0LjEzMTEgMTQuMTk3NCA0My43NjA0IDEzLjk3MTZDNDMuMzkzOSAxMy43NDU3IDQyLjk2MzUgMTMuNjMyOCA0Mi40NjkyIDEzLjYzMjhDNDEuOTMyMiAxMy42MzI4IDQxLjQ2MzUgMTMuNzY0OSA0MS4wNjI5IDE0LjAyOTFDNDAuNjY2NiAxNC4yODkxIDQwLjM1OTggMTQuNjMgNDAuMTQyNSAxNS4wNTE4QzM5LjkyOTQgMTUuNDY5NSAzOS44MjI5IDE1LjkyMzMgMzkuODIyOSAxNi40MTM0VjE3LjUzMkMzOS44MjI5IDE4LjE4ODIgMzkuOTM3OSAxOC43NDY0IDQwLjE2OCAxOS4yMDY3QzQwLjQwMjQgMTkuNjY2OSA0MC43Mjg0IDIwLjAxODUgNDEuMTQ2IDIwLjI2MTRDNDEuNTYzNiAyMC41IDQyLjA1MTYgMjAuNjE5MyA0Mi42MDk4IDIwLjYxOTNDNDIuOTcyIDIwLjYxOTMgNDMuMzAyMyAyMC41NjgyIDQzLjYwMDYgMjAuNDY1OUM0My44OTg5IDIwLjM1OTQgNDQuMTU2NyAyMC4yMDE3IDQ0LjM3NCAxOS45OTI5QzQ0LjU5MTMgMTkuNzg0MSA0NC43NTc1IDE5LjUyNjMgNDQuODcyNiAxOS4yMTk1TDQ2LjY4MTUgMTkuNTQ1NUM0Ni41MzY3IDIwLjA3ODEgNDYuMjc2NyAyMC41NDQ3IDQ1LjkwMTcgMjAuOTQ1M0M0NS41MzEgMjEuMzQxNiA0NS4wNjQ0IDIxLjY1MDYgNDQuNTAxOSAyMS44NzIyQzQzLjk0MzYgMjIuMDg5NSA0My4zMDY1IDIyLjE5ODIgNDIuNTkwNiAyMi4xOTgyWk00OS4xMDM1IDIyVjEyLjE4MThINTAuOTUwOVYxMy43NDE1SDUxLjA1MzFDNTEuMjMyMSAxMy4yMTMxIDUxLjU0NzQgMTIuNzk3NiA1MS45OTkxIDEyLjQ5NUM1Mi40NTUxIDEyLjE4ODIgNTIuOTcwNyAxMi4wMzQ4IDUzLjU0NiAxMi4wMzQ4QzUzLjY2NTMgMTIuMDM0OCA1My44MDYgMTIuMDM5MSA1My45Njc5IDEyLjA0NzZDNTQuMTM0MSAxMi4wNTYxIDU0LjI2NDEgMTIuMDY2OCA1NC4zNTc4IDEyLjA3OTVWMTMuOTA3N0M1NC4yODExIDEzLjg4NjQgNTQuMTQ0NyAxMy44NjI5IDUzLjk0ODcgMTMuODM3NEM1My43NTI3IDEzLjgwNzUgNTMuNTU2NyAxMy43OTI2IDUzLjM2MDcgMTMuNzkyNkM1Mi45MDg5IDEzLjc5MjYgNTIuNTA2MiAxMy44ODg1IDUyLjE1MjYgMTQuMDgwM0M1MS44MDMxIDE0LjI2NzggNTEuNTI2MSAxNC41Mjk4IDUxLjMyMTYgMTQuODY2NUM1MS4xMTcgMTUuMTk4OSA1MS4wMTQ4IDE1LjU3ODEgNTEuMDE0OCAxNi4wMDQzVjIySDQ5LjEwMzVaTTYwLjA3MTcgMjIuMTk4MkM1OS4xMDQ0IDIyLjE5ODIgNTguMjcxMyAyMS45OTE1IDU3LjU3MjQgMjEuNTc4MUM1Ni44Nzc4IDIxLjE2MDUgNTYuMzQwOSAyMC41NzQ2IDU1Ljk2MTYgMTkuODIwM0M1NS41ODY2IDE5LjA2MTggNTUuMzk5MSAxOC4xNzMzIDU1LjM5OTEgMTcuMTU0OEM1NS4zOTkxIDE2LjE0OTEgNTUuNTg2NiAxNS4yNjI4IDU1Ljk2MTYgMTQuNDk1N0M1Ni4zNDA5IDEzLjcyODcgNTYuODY5MyAxMy4xMyA1Ny41NDY5IDEyLjY5OTZDNTguMjI4NyAxMi4yNjkyIDU5LjAyNTYgMTIuMDU0IDU5LjkzNzUgMTIuMDU0QzYwLjQ5MTUgMTIuMDU0IDYxLjAyODQgMTIuMTQ1NiA2MS41NDgzIDEyLjMyODhDNjIuMDY4MiAxMi41MTIxIDYyLjUzNDggMTIuNzk5NyA2Mi45NDgxIDEzLjE5MThDNjMuMzYxNSAxMy41ODM4IDYzLjY4NzUgMTQuMDkzIDYzLjkyNjEgMTQuNzE5NUM2NC4xNjQ4IDE1LjM0MTYgNjQuMjg0MSAxNi4wOTggNjQuMjg0MSAxNi45ODg2VjE3LjY2NjJINTYuNDc5NFYxNi4yMzQ0SDYyLjQxMTJDNjIuNDExMiAxNS43MzE1IDYyLjMwODkgMTUuMjg2MiA2Mi4xMDQ0IDE0Ljg5ODRDNjEuODk5OCAxNC41MDY0IDYxLjYxMjIgMTQuMTk3NCA2MS4yNDE1IDEzLjk3MTZDNjAuODc1IDEzLjc0NTcgNjAuNDQ0NiAxMy42MzI4IDU5Ljk1MDMgMTMuNjMyOEM1OS40MTMzIDEzLjYzMjggNTguOTQ0NiAxMy43NjQ5IDU4LjU0NCAxNC4wMjkxQzU4LjE0NzcgMTQuMjg5MSA1Ny44NDA5IDE0LjYzIDU3LjYyMzYgMTUuMDUxOEM1Ny40MTA1IDE1LjQ2OTUgNTcuMzA0IDE1LjkyMzMgNTcuMzA0IDE2LjQxMzRWMTcuNTMyQzU3LjMwNCAxOC4xODgyIDU3LjQxOSAxOC43NDY0IDU3LjY0OTEgMTkuMjA2N0M1Ny44ODM1IDE5LjY2NjkgNTguMjA5NSAyMC4wMTg1IDU4LjYyNzEgMjAuMjYxNEM1OS4wNDQ3IDIwLjUgNTkuNTMyNyAyMC42MTkzIDYwLjA5MDkgMjAuNjE5M0M2MC40NTMxIDIwLjYxOTMgNjAuNzgzNCAyMC41NjgyIDYxLjA4MTcgMjAuNDY1OUM2MS4zOCAyMC4zNTk0IDYxLjYzNzggMjAuMjAxNyA2MS44NTUxIDE5Ljk5MjlDNjIuMDcyNCAxOS43ODQxIDYyLjIzODYgMTkuNTI2MyA2Mi4zNTM3IDE5LjIxOTVMNjQuMTYyNiAxOS41NDU1QzY0LjAxNzcgMjAuMDc4MSA2My43NTc4IDIwLjU0NDcgNjMuMzgyOCAyMC45NDUzQzYzLjAxMjEgMjEuMzQxNiA2Mi41NDU0IDIxLjY1MDYgNjEuOTgyOSAyMS44NzIyQzYxLjQyNDcgMjIuMDg5NSA2MC43ODc2IDIyLjE5ODIgNjAuMDcxNyAyMi4xOTgyWk03MC4yNjAxIDIyLjE5MThDNjkuNDY3NSAyMi4xOTE4IDY4Ljc2MDEgMjEuOTg5MyA2OC4xMzc5IDIxLjU4NDVDNjcuNTIgMjEuMTc1NCA2Ny4wMzQyIDIwLjU5MzcgNjYuNjgwNSAxOS44Mzk1QzY2LjMzMTEgMTkuMDgxIDY2LjE1NjQgMTguMTcxMiA2Ni4xNTY0IDE3LjExMDFDNjYuMTU2NCAxNi4wNDkgNjYuMzMzMiAxNS4xNDEzIDY2LjY4NjkgMTQuMzg3MUM2Ny4wNDQ5IDEzLjYzMjggNjcuNTM0OSAxMy4wNTU0IDY4LjE1NzEgMTIuNjU0OEM2OC43NzkyIDEyLjI1NDMgNjkuNDg0NSAxMi4wNTQgNzAuMjcyOSAxMi4wNTRDNzAuODgyMiAxMi4wNTQgNzEuMzcyMyAxMi4xNTYyIDcxLjc0MyAxMi4zNjA4QzcyLjExOCAxMi41NjExIDcyLjQwNzggMTIuNzk1NSA3Mi42MTIzIDEzLjA2MzlDNzIuODIxMSAxMy4zMzI0IDcyLjk4MzEgMTMuNTY4OSA3My4wOTgxIDEzLjc3MzRINzMuMjEzMlY4LjkwOTA5SDc1LjEyNDRWMjJINzMuMjU3OVYyMC40NzIzSDczLjA5ODFDNzIuOTgzMSAyMC42ODExIDcyLjgxNjkgMjAuOTE5NyA3Mi41OTk2IDIxLjE4ODJDNzIuMzg2NSAyMS40NTY3IDcyLjA5MjUgMjEuNjkxMSA3MS43MTc1IDIxLjg5MTNDNzEuMzQyNSAyMi4wOTE2IDcwLjg1NjcgMjIuMTkxOCA3MC4yNjAxIDIyLjE5MThaTTcwLjY4MTkgMjAuNTYxOEM3MS4yMzE3IDIwLjU2MTggNzEuNjk2MSAyMC40MTY5IDcyLjA3NTQgMjAuMTI3MUM3Mi40NTg5IDE5LjgzMzEgNzIuNzQ4NyAxOS40MjYxIDcyLjk0NDcgMTguOTA2MkM3My4xNDUgMTguMzg2NCA3My4yNDUyIDE3Ljc4MTIgNzMuMjQ1MiAxNy4wOTA5QzczLjI0NTIgMTYuNDA5MSA3My4xNDcxIDE1LjgxMjUgNzIuOTUxMSAxNS4zMDExQzcyLjc1NTEgMTQuNzg5OCA3Mi40Njc1IDE0LjM5MTMgNzIuMDg4MiAxNC4xMDU4QzcxLjcwODkgMTMuODIwMyA3MS4yNDAyIDEzLjY3NzYgNzAuNjgxOSAxMy42Nzc2QzcwLjEwNjcgMTMuNjc3NiA2OS42MjczIDEzLjgyNjcgNjkuMjQzNyAxNC4xMjVDNjguODYwMiAxNC40MjMzIDY4LjU3MDQgMTQuODMwMyA2OC4zNzQ0IDE1LjM0NTlDNjguMTgyNyAxNS44NjE1IDY4LjA4NjggMTYuNDQzMiA2OC4wODY4IDE3LjA5MDlDNjguMDg2OCAxNy43NDcyIDY4LjE4NDggMTguMzM3NCA2OC4zODA4IDE4Ljg2MTVDNjguNTc2OCAxOS4zODU3IDY4Ljg2NjYgMTkuODAxMSA2OS4yNTAxIDIwLjEwOEM2OS42Mzc5IDIwLjQxMDUgNzAuMTE1MiAyMC41NjE4IDcwLjY4MTkgMjAuNTYxOFpNODMuMTI5MyAyMlY4LjkwOTA5SDg1LjA0MDVWMTMuNzczNEg4NS4xNTU2Qzg1LjI2NjQgMTMuNTY4OSA4NS40MjYyIDEzLjMzMjQgODUuNjM1IDEzLjA2MzlDODUuODQzOCAxMi43OTU1IDg2LjEzMzYgMTIuNTYxMSA4Ni41MDQzIDEyLjM2MDhDODYuODc1IDEyLjE1NjIgODcuMzY1MSAxMi4wNTQgODcuOTc0NSAxMi4wNTRDODguNzY3MSAxMi4wNTQgODkuNDc0NSAxMi4yNTQzIDkwLjA5NjYgMTIuNjU0OEM5MC43MTg4IDEzLjA1NTQgOTEuMjA2NyAxMy42MzI4IDkxLjU2MDQgMTQuMzg3MUM5MS45MTg0IDE1LjE0MTMgOTIuMDk3MyAxNi4wNDkgOTIuMDk3MyAxNy4xMTAxQzkyLjA5NzMgMTguMTcxMiA5MS45MjA1IDE5LjA4MSA5MS41NjY4IDE5LjgzOTVDOTEuMjEzMSAyMC41OTM3IDkwLjcyNzMgMjEuMTc1NCA5MC4xMDk0IDIxLjU4NDVDODkuNDkxNSAyMS45ODkzIDg4Ljc4NjMgMjIuMTkxOCA4Ny45OTM3IDIyLjE5MThDODcuMzk3MSAyMi4xOTE4IDg2LjkwOTEgMjIuMDkxNiA4Ni41Mjk5IDIxLjg5MTNDODYuMTU0OSAyMS42OTExIDg1Ljg2MDggMjEuNDU2NyA4NS42NDc4IDIxLjE4ODJDODUuNDM0NyAyMC45MTk3IDg1LjI3MDYgMjAuNjgxMSA4NS4xNTU2IDIwLjQ3MjNIODQuOTk1OFYyMkg4My4xMjkzWk04NS4wMDIyIDE3LjA5MDlDODUuMDAyMiAxNy43ODEyIDg1LjEwMjMgMTguMzg2NCA4NS4zMDI2IDE4LjkwNjJDODUuNTAyOSAxOS40MjYxIDg1Ljc5MjcgMTkuODMzMSA4Ni4xNzE5IDIwLjEyNzFDODYuNTUxMiAyMC40MTY5IDg3LjAxNTcgMjAuNTYxOCA4Ny41NjU0IDIwLjU2MThDODguMTM2NCAyMC41NjE4IDg4LjYxMzcgMjAuNDEwNSA4OC45OTcyIDIwLjEwOEM4OS4zODA3IDE5LjgwMTEgODkuNjcwNSAxOS4zODU3IDg5Ljg2NjUgMTguODYxNUM5MC4wNjY4IDE4LjMzNzQgOTAuMTY2OSAxNy43NDcyIDkwLjE2NjkgMTcuMDkwOUM5MC4xNjY5IDE2LjQ0MzIgOTAuMDY4OSAxNS44NjE1IDg5Ljg3MjkgMTUuMzQ1OUM4OS42ODEyIDE0LjgzMDMgODkuMzkxNCAxNC40MjMzIDg5LjAwMzYgMTQuMTI1Qzg4LjYyMDEgMTMuODI2NyA4OC4xNDA3IDEzLjY3NzYgODcuNTY1NCAxMy42Nzc2Qzg3LjAxMTQgMTMuNjc3NiA4Ni41NDI3IDEzLjgyMDMgODYuMTU5MSAxNC4xMDU4Qzg1Ljc3OTkgMTQuMzkxMyA4NS40OTIyIDE0Ljc4OTggODUuMjk2MiAxNS4zMDExQzg1LjEwMDIgMTUuODEyNSA4NS4wMDIyIDE2LjQwOTEgODUuMDAyMiAxNy4wOTA5Wk05NS4yOTc2IDI1LjY4MThDOTUuMDEyMSAyNS42ODE4IDk0Ljc1MjEgMjUuNjU4NCA5NC41MTc4IDI1LjYxMTVDOTQuMjgzNCAyNS41Njg5IDk0LjEwODcgMjUuNTIyIDkzLjk5MzYgMjUuNDcwOUw5NC40NTM4IDIzLjkwNDhDOTQuODAzMyAyMy45OTg2IDk1LjExNDMgMjQuMDM5MSA5NS4zODcxIDI0LjAyNjNDOTUuNjU5OCAyNC4wMTM1IDk1LjkwMDYgMjMuOTExMiA5Ni4xMDk0IDIzLjcxOTVDOTYuMzIyNCAyMy41Mjc3IDk2LjUwOTkgMjMuMjE0NSA5Ni42NzE5IDIyLjc3OThMOTYuOTA4NCAyMi4xMjc4TDkzLjMxNjEgMTIuMTgxOEg5NS4zNjE1TDk3Ljg0OCAxOS44MDExSDk3Ljk1MDNMMTAwLjQzNyAxMi4xODE4SDEwMi40ODlMOTguNDQyNSAyMy4zMTA0Qzk4LjI1NSAyMy44MjE3IDk4LjAxNjMgMjQuMjU0MyA5Ny43MjY2IDI0LjYwOEM5Ny40MzY4IDI0Ljk2NTkgOTcuMDkxNiAyNS4yMzQ0IDk2LjY5MTEgMjUuNDEzNEM5Ni4yOTA1IDI1LjU5MjMgOTUuODI2IDI1LjY4MTggOTUuMjk3NiAyNS42ODE4WiIgZmlsbD0iIzE4MUEyNyIvPgo8L3N2Zz4K";
const styles$2 = css`
  :host {
    --color-gray-300: #fbfbfb;
    --color-gray-600: #5b5959;
    --color-gray-700: #979797;
    --color-gray-750: #dadada;
    --color-gray-800: #f3f3f3;
    --color-blue-400: #006ad1;
    --color-red-400: #c42d32;
    --color-white: #ffffff;
    --shadow-background: #141414;
    --shadow-opacity: 0.15;

    left: -10000px;
    position: absolute;
    top: -10000px;
    visibility: hidden;
  }

  * {
    box-sizing: border-box;
  }

  :host([open]) {
    visibility: visible;
  }

  :host([open]) .shadow {
    opacity: var(--shadow-opacity);
    visibility: visible;
  }

  button {
    appearance: button;
    font-family: inherit;
    font-size: 100%;
    line-height: 1.15;
  }

  .verify {
    background-color: var(--color-gray-300);
    border-radius: 3px;
    box-shadow: 0 32px 42px 0 rgba(0, 0, 0, 0.2), 0 2px 16px 0 rgba(0, 0, 0, 0.08);
    font-family: 'Inter', sans-serif;
    max-height: calc(100vh - 32px);
    position: fixed;
    min-width: 340px;
    width: min-content;
    z-index: var(--olo-auth-verify-z-index, 9999);
  }

  .container {
    border-radius: 3px;
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 32px);
    overflow: auto;
  }

  .header {
    background-color: var(--color-gray-300);
    border-bottom: 1px solid #dadada;
    box-shadow: rgba(0, 0, 0, 0.08) 0px -2px 8px;
    flex: 0;
    padding: 16px;
  }

  .header h1 {
    align-items: center;
    display: flex;
    font-size: 18px;
    font-weight: 600;
    justify-content: space-between;
    line-height: 1.5;
    margin: 0 0 4px 0;
  }

  .header h2 {
    color: var(--color-gray-600);
    font-size: 16px;
    font-weight: 400;
    line-height: 1.5;
    margin: 4px 0 0 0;
  }

  .close {
    border: none;
    background: transparent;
    cursor: pointer;
    display: inline-block;
    margin: -16px;
    padding: 16px;
    position: relative;
    text-indent: -9999px;
  }

  .close::before {
    background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0ibTkuNjk3IDggNS4xMjktNS4xMjggMS4wNTctMS4wNTdhLjQuNCAwIDAgMCAwLS41NjZMMTQuNzUyLjExOGEuNC40IDAgMCAwLS41NjYgMEw4IDYuMzAzIDEuODE1LjExN2EuNC40IDAgMCAwLS41NjYgMEwuMTE3IDEuMjQ5YS40LjQgMCAwIDAgMCAuNTY1TDYuMzAzIDggLjExNyAxNC4xODZhLjQuNCAwIDAgMCAwIC41NjZsMS4xMzIgMS4xMzFhLjQuNCAwIDAgMCAuNTY1IDBMOCA5LjY5N2w1LjEyOCA1LjEyOSAxLjA1OCAxLjA1N2EuNC40IDAgMCAwIC41NjYgMGwxLjEzMS0xLjEzMWEuNC40IDAgMCAwIDAtLjU2Nkw5LjY5NyA4WiIgZmlsbD0iIzE0MTQxNCIvPjwvc3ZnPg==');
    content: '';
    display: block;
    height: 16px;
    width: 16px;
  }

  .enterCode {
    background-color: var(--color-white);
    flex: 1 1 auto;
    padding: 8px 16px 16px 16px;
  }

  .enterCodeMessage {
    font-size: 16px;
    line-height: 1.5;
    margin: 0;
  }

  .loginHintErrorMessage {
    color: #c42d32;
    font-size: 16px;
    line-height: 1.5;
    margin: 16px 0;
    text-align: center;
  }

  .enterCodeForm {
    margin: 16px 0 0 0;
  }

  ::slotted(.enterCodeLabel) {
    display: block;
    margin-bottom: 4px;
  }

  ::slotted(.enterCodeInput:focus) {
    border-color: var(--color-blue-400) !important;
    outline: 0 !important;
  }

  .visuallyHidden {
    position: absolute;
    clip: rect(0 0 0 0);
  }

  .sendAgainMessage {
    font-size: 16px;
    line-height: 1.5;
    margin: 16px 0;
    text-align: center;
  }

  .codeSent {
    font-size: 16px;
    align-items: center;
    background-color: var(--color-gray-800);
    border-radius: 3px;
    display: flex;
    margin-top: 16px;
    padding: 8px 12px;
  }

  .codeSentIcon--email,
  .codeSentIcon--sms {
    align-items: center;
    display: inline-flex;
    margin-right: 8px;
  }

  .codeSentIcon--email::before,
  .codeSentIcon--sms::before {
    content: '';
    display: inline-block;
    height: 16px;
  }

  .codeSentIcon--email::before {
    background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA1MTIgNTEyIj48IS0tISBGb250IEF3ZXNvbWUgUHJvIDYuMS4xIGJ5IEBmb250YXdlc29tZSAtIGh0dHBzOi8vZm9udGF3ZXNvbWUuY29tIExpY2Vuc2UgLSBodHRwczovL2ZvbnRhd2Vzb21lLmNvbS9saWNlbnNlIChDb21tZXJjaWFsIExpY2Vuc2UpIENvcHlyaWdodCAyMDIyIEZvbnRpY29ucywgSW5jLi0tPjxwYXRoIGQ9Ik00NjQgNjRjMjYuNSAwIDQ4IDIxLjQ5IDQ4IDQ4IDAgMTUuMS03LjEgMjkuMy0xOS4yIDM4LjRMMjc1LjIgMzEzLjZhMzIuMSAzMi4xIDAgMCAxLTM4LjQgMEwxOS4yIDE1MC40QzcuMTEzIDE0MS4zIDAgMTI3LjEgMCAxMTJjMC0yNi41MSAyMS40OS00OCA0OC00OGg0MTZ6TTIxNy42IDMzOS4yYTYzLjkgNjMuOSAwIDAgMCA3Ni44IDBMNTEyIDE3NnYyMDhjMCAzNS4zLTI4LjcgNjQtNjQgNjRINjRjLTM1LjM1IDAtNjQtMjguNy02NC02NFYxNzZsMjE3LjYgMTYzLjJ6Ii8+PC9zdmc+');
    width: 16px;
  }

  .codeSentIcon--sms::before {
    background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAzODQgNTEyIj48IS0tISBGb250IEF3ZXNvbWUgUHJvIDYuMS4xIGJ5IEBmb250YXdlc29tZSAtIGh0dHBzOi8vZm9udGF3ZXNvbWUuY29tIExpY2Vuc2UgLSBodHRwczovL2ZvbnRhd2Vzb21lLmNvbS9saWNlbnNlIChDb21tZXJjaWFsIExpY2Vuc2UpIENvcHlyaWdodCAyMDIyIEZvbnRpY29ucywgSW5jLi0tPjxwYXRoIGQ9Ik0zMDQgMEg4MEM0NC42NSAwIDE2IDI4LjY1IDE2IDY0djM4NGMwIDM1LjM1IDI4LjY1IDY0IDY0IDY0aDIyNGMzNS4zNSAwIDY0LTI4LjY1IDY0LTY0VjY0YzAtMzUuMzUtMjguNy02NC02NC02NHpNMTkyIDQ4MGMtMTcuNzUgMC0zMi0xNC4yNS0zMi0zMnMxNC4yNS0zMiAzMi0zMiAzMiAxNC4yNSAzMiAzMi0xNC4yIDMyLTMyIDMyek0zMDQgNjR2MzIwSDgwVjY0aDIyNHoiLz48L3N2Zz4=');
    width: 12px;
  }

  .codeSentText {
    line-height: 1.5;
  }

  .resendButton {
    background: transparent;
    border: 0;
    color: var(--color-blue-400);
    cursor: pointer;
    display: inline-block;
    margin: 0;
    padding: 0;
    text-decoration: underline;
  }

  .switchMethod {
    font-size: 16px;
    background: transparent;
    border: 0;
    color: var(--color-blue-400);
    cursor: pointer;
    display: block;
    margin: 8px 0;
    padding: 8px;
    text-align: center;
    text-decoration: underline;
    width: 100%;
  }

  .redirect {
    font-size: 16px;
    line-height: 1.5;
    margin: 0;
  }

  .poweredByOlo,
  .loader {
    margin: 16px auto;
  }

  .loader,
  .loader:after {
    border-radius: 50%;
    width: 32px;
    height: 32px;
  }

  .loader {
    margin: 16px auto;
    font-size: 12.4px;
    position: relative;
    overflow: hidden;
    border-top: 4.8px solid var(--color-gray-750);
    border-right: 4.8px solid var(--color-gray-750);
    border-bottom: 4.8px solid var(--color-gray-750);
    border-left: 4.8px solid var(--color-blue-400);
    transform: translateZ(0);
    animation: spin 550ms infinite linear;
  }

  .arrow {
    border-bottom: 5px solid transparent;
    border-right: 8px solid var(--color-white);
    border-top: 5px solid transparent;
    position: absolute;
    transform: translateX(-100%);
    z-index: 10;
  }

  .shadow {
    background: var(--shadow-background);
    opacity: 0;
    position: fixed;
    transition: opacity 200ms linear, visibility 200ms linear;
    visibility: hidden;
  }

  .shadow.primary {
    bottom: var(--olo-auth-verify-primary-shadow-bottom, 0);
    height: var(--olo-auth-verify-primary-shadow-height);
    left: var(--olo-auth-verify-primary-shadow-left, 0);
    right: var(--olo-auth-verify-primary-shadow-right, 0);
    top: var(--olo-auth-verify-primary-shadow-top, 0);
    width: var(--olo-auth-verify-primary-shadow-width);
    z-index: var(--olo-auth-verify-primary-shadow-z-index, 9998);
  }

  .shadow.secondary {
    bottom: var(--olo-auth-verify-secondary-shadow-bottom);
    height: var(--olo-auth-verify-secondary-shadow-height);
    left: var(--olo-auth-verify-secondary-shadow-left, 0);
    right: var(--olo-auth-verify-secondary-shadow-right, 0);
    top: var(--olo-auth-verify-secondary-shadow-top, 0);
    width: var(--olo-auth-verify-secondary-shadow-width);
    z-index: var(--olo-auth-verify-secondary-shadow-z-index, 9998);
  }

  .error {
    align-items: center;
    color: var(--color-red-400);
    display: flex;
    font-size: 12.4px;
    margin-top: 4px;
  }

  .error::before {
    background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTQiIGhlaWdodD0iMTQiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik0xMy4wMDQgN0E2LjAwMyA2LjAwMyAwIDEgMSAuOTk3IDdhNi4wMDMgNi4wMDMgMCAwIDEgMTIuMDA3IDBaTTcgOC4yMWExLjExNCAxLjExNCAwIDEgMCAwIDIuMjI3QTEuMTE0IDEuMTE0IDAgMCAwIDcgOC4yMVptLS44NzgtLjcxLS4xOC0zLjI5M2EuMjkuMjkgMCAwIDEgLjI5LS4zMDZoMS41MzVjLjE2NyAwIC4zLjE0LjI5LjMwNkw3Ljg3NyA3LjVhLjI5LjI5IDAgMCAxLS4yOS4yNzVINi40MTJhLjI5LjI5IDAgMCAxLS4yOS0uMjc1WiIgZmlsbD0iI0M0MkQzMiIvPjwvc3ZnPg==');
    background-size: cover;
    content: '';
    display: inline-block;
    height: 14px;
    margin-right: 4px;
    width: 14px;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;
var __defProp$2 = Object.defineProperty;
var __getOwnPropDesc$2 = Object.getOwnPropertyDescriptor;
var __decorateClass$2 = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc$2(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp$2(target, key, result);
  return result;
};
var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateAdd = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateMethod = (obj, member, method) => {
  __accessCheck(obj, member, "access private method");
  return method;
};
var _createMfaComponent, createMfaComponent_fn;
const mapLoginError = (description, status) => {
  if (status === 429) {
    return "Too many attempts. Try again later.";
  }
  switch (description) {
    case "invalid_code":
      return "Incorrect code. Please try again.";
    case "unknown":
      return "Error submitting code. Please try again.";
    default:
      return null;
  }
};
const mapCodeError = (_, status) => {
  if (status === 429) {
    return "Tried to send a new code too many times. Try again later.";
  }
  return "Error sending code. Try again later.";
};
const addDashes = (str) => str.split("").join("-");
const createInput = () => {
  const input = document.createElement("input");
  Object.assign(input.style, {
    border: "1px solid var(--color-gray-700)",
    borderRadius: "3px",
    boxSizing: "border-box",
    font: "inherit",
    fontFamily: `'Inter', sans-serif`,
    fontSize: "18px",
    fontWeight: "400",
    lineHeight: "1.5",
    margin: "0",
    padding: "11px 16px",
    touchAction: "none",
    verticalAlign: "baseline",
    width: "100%"
  });
  input.slot = "input";
  input.setAttribute("aria-errormessage", "code-error");
  input.className = "enterCodeInput";
  input.name = "code";
  input.id = "VerifyAccountOverlayCode";
  input.autocomplete = "one-time-code";
  input.autocapitalize = "none";
  input.inputMode = "numeric";
  return input;
};
const createLabel = () => {
  const label = document.createElement("label");
  label.htmlFor = "VerifyAccountOverlayCode";
  label.className = "enterCodeLabel";
  label.innerHTML = `Enter Code`;
  return label;
};
const defaultOptions = {
  useSeparatedInputs: false
};
let VerifyAccountOverlay$1 = class VerifyAccountOverlay extends LitElement {
  constructor(options = defaultOptions) {
    super();
    __privateAdd(this, _createMfaComponent);
    this["data-test-oloAuthPrompt"] = "";
    this["data-testid"] = "oloAuthPrompt";
    this.open = false;
    this.useSeparatedInputs = false;
    this._channelId = "";
    this._email = "";
    this._method = CodeMethod.Text;
    this._phoneNumber = "";
    this._loginUrl = "";
    this._loginProviderSlug = "";
    this._code = "";
    this._disabled = false;
    this._codeRequest = new RequestStatusController(this, sendCode, mapCodeError);
    this._loginRequest = new RequestStatusController(this, otpLogin, mapLoginError);
    this._cleanup = null;
    this._verifyRef = createRef();
    this._arrowRef = createRef();
    this._targetEl = null;
    this._hasSent = false;
    this._labelEl = this.useSeparatedInputs ? void 0 : createLabel();
    this._handleCodeChange = (e) => {
      const input = e.target;
      this._code = input.value = input.value.replace(/[^0-9]{1,}/g, "").slice(0, 6);
      if (this._code.length === 6) {
        this._login();
      }
    };
    this._handleKeyup = (e) => {
      if (!this.open)
        return;
      if (e.key.toLowerCase() === "escape")
        this._handleClose();
    };
    this._computePosition = async () => {
      var _a;
      if (!this._targetEl || !this._arrowRef.value || !this._verifyRef.value || !this.open) {
        return;
      }
      const verifyEl = this._verifyRef.value;
      const arrowEl = this._arrowRef.value;
      const {
        x,
        y,
        middlewareData: { arrow: { x: arrowX, y: arrowY } = {} }
      } = await computePosition(this._targetEl, verifyEl, {
        middleware: [offset(10), arrow({ element: arrowEl })],
        placement: "right-start",
        strategy: "fixed"
      });
      if ((((_a = this._verifyRef.value) == null ? void 0 : _a.clientWidth) || 0) + x > window.innerWidth) {
        Object.assign(verifyEl.style, {
          left: `50%`,
          top: `50%`,
          transform: "translate(-50%, -50%)"
        });
        Object.assign(arrowEl.style, {
          display: "none"
        });
      } else {
        Object.assign(verifyEl.style, {
          left: `${x}px`,
          top: `${y}px`,
          transform: ""
        });
        Object.assign(arrowEl.style, {
          display: "block",
          left: `${arrowX != null ? arrowX : 0}px`,
          top: `${arrowY != null ? arrowY : 0}px`
        });
      }
    };
    this.useSeparatedInputs = Boolean(options.useSeparatedInputs);
    this._inputEl = this.useSeparatedInputs ? __privateMethod(this, _createMfaComponent, createMfaComponent_fn).call(this) : createInput();
  }
  setStyles(customStyles) {
    this.customStyles = customStyles;
  }
  get _screenReaderIntro() {
    if (this._phoneNumber && this._method === CodeMethod.Text) {
      return `To check out with your saved details, enter the verification code sent to the
        mobile number ending with: ${addDashes(this._phoneNumber.slice(-4))}.`;
    }
    if (this._email && this._method === CodeMethod.Email) {
      return `To check out with your saved details, enter the verification code sent to ${this._email}`;
    }
    return "Check out with your saved details using your Olo Account";
  }
  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("keyup", this._handleKeyup);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("keyup", this._handleKeyup);
  }
  updated() {
    var _a, _b, _c;
    const isReadonly = !!(((_a = this._codeRequest) == null ? void 0 : _a.loading) || ((_b = this._loginRequest) == null ? void 0 : _b.loading) || ((_c = this._loginRequest) == null ? void 0 : _c.success));
    this._inputEl.toggleAttribute("readonly", isReadonly);
  }
  async attach(targetEl, {
    email,
    phoneNumber,
    channelId,
    loginUrl,
    loginProviderSlug
  }) {
    try {
      this.detach();
      if (this.useSeparatedInputs && this._inputEl instanceof OloCodeVerification) {
        this._inputEl.setStyles(this.customStyles);
      }
      this._email = email;
      this._phoneNumber = phoneNumber;
      this._channelId = channelId;
      this._loginUrl = loginUrl;
      this._loginProviderSlug = loginProviderSlug;
      await this._sendCode();
      if (!targetEl.isConnected) {
        this.detach();
        return;
      }
      this._targetEl = targetEl;
      document.body.appendChild(this);
      this._inputEl.addEventListener(
        "input",
        this.useSeparatedInputs ? () => {
        } : this._handleCodeChange
      );
      if (this._labelEl) {
        this.insertAdjacentElement("beforeend", this._labelEl);
      }
      this.insertAdjacentElement("beforeend", this._inputEl);
      this.show();
      await this.updateComplete;
      this._cleanup = autoUpdate(
        this._targetEl,
        this._verifyRef.value,
        this._computePosition
      );
    } catch (e) {
      this._emit("error", {
        error: new VerifyAttachError("Failed to attach overlay", { cause: e })
      });
    }
  }
  async detach() {
    var _a, _b, _c, _d, _e;
    try {
      this.open = false;
      (_a = this._cleanup) == null ? void 0 : _a.call(this);
      (_b = this.parentElement) == null ? void 0 : _b.removeChild(this);
      (_c = this._labelEl) == null ? void 0 : _c.remove();
      (_d = this._inputEl) == null ? void 0 : _d.removeEventListener("input", this._handleCodeChange);
      (_e = this._inputEl) == null ? void 0 : _e.remove();
    } catch (e) {
      this._emit("error", {
        error: new VerifyDetachError("Failed to detach overlay", { cause: e })
      });
    }
  }
  async hide() {
    var _a;
    try {
      if (!this.open)
        return;
      (_a = this._inputEl) == null ? void 0 : _a.blur();
      this._emit("close");
      this._code = "";
      this.open = false;
    } catch (e) {
      this._emit("error", {
        error: new VerifyHideError("Failed to hide overlay", { cause: e })
      });
    }
  }
  async show() {
    var _a;
    try {
      if (this.open)
        return;
      this._emit("open");
      this.open = true;
      this._computePosition();
      await this.updateComplete;
      await ((_a = this._inputEl) == null ? void 0 : _a.focus());
    } catch (e) {
      this._emit("error", {
        error: new VerifyShowError("Failed to show overlay", { cause: e })
      });
    }
  }
  render() {
    var _a, _b;
    try {
      return html`
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600&display=swap');
        </style>
        <div class="verify" ${ref(this._verifyRef)}>
          <div class="arrow" ${ref(this._arrowRef)}></div>
          <div class="container">
            <header class="header">
              <h1>
                Sign In
                <button
                  @click=${this._handleClose}
                  type="button"
                  title="Close"
                  class="close"
                ></button>
              </h1>
              <h2 title="Email">${this._email}</h2>
            </header>
            <section class="enterCode">
              <p class="enterCodeMessage">
                Enter the code sent to your
                ${this._method === CodeMethod.Text ? "phone" : "email"} to access your
                saved details.
              </p>
              ${this._renderForm()}
            </section>
            ${((_a = this._loginRequest) == null ? void 0 : _a.loading) || ((_b = this._loginRequest) == null ? void 0 : _b.success) ? this._renderLoader() : this._renderPoweredByOlo()}
          </div>
        </div>
        ${this._renderShadow()}
      `;
    } catch (e) {
      this._emit("error", {
        error: new VerifyRenderError("Failed to render overlay", { cause: e })
      });
    }
    return html``;
  }
  _renderLoader() {
    return html`<div role="progressbar" class="loader"></div>`;
  }
  _renderForm() {
    var _a;
    return html`<form
      class="enterCodeForm"
      @submit="${(e) => e.preventDefault()}"
    >
      <slot name="label"></slot>
      <span class="visuallyHidden" aria-live="polite">
        (${this._screenReaderIntro})
      </span>
      <slot name="input"></slot>
      ${this._renderVerifyError()}
      <div class="sendAgainMessage">
        Didn't get it?
        <button @click=${this._handleResend} type="button" class="resendButton">
          Resend code</button
        >.
      </div>
      ${((_a = this._codeRequest) == null ? void 0 : _a.success) ? this._renderCodeSentMessage() : ""}
      <button
        type="button"
        @click=${this._handleToggle}
        class="switchMethod"
        title="Switch verification method"
      >
        Send code by ${this._method === CodeMethod.Email ? "text" : "email"} instead
      </button>
      <p class="redirect">
        You'll be redirected to the checkout screen to review your order.
      </p>
    </form>`;
  }
  _renderPoweredByOlo() {
    return html` <span class="poweredByOlo" data-testid="powered-by-olo">
      <img src=${logo$1} alt="Olo" height="19" />
    </span>`;
  }
  _renderVerifyError() {
    var _a, _b, _c, _d, _e, _f;
    if (!((_a = this._codeRequest) == null ? void 0 : _a.error) && !((_b = this._loginRequest) == null ? void 0 : _b.error))
      return "";
    return html`
      <div class="error" id="code-error" role="alert">
        ${((_d = (_c = this._codeRequest) == null ? void 0 : _c.error) == null ? void 0 : _d.message) || ((_f = (_e = this._loginRequest) == null ? void 0 : _e.error) == null ? void 0 : _f.message)}
      </div>
    `;
  }
  _renderCodeSentMessage() {
    const text = `Code sent to ${this._method === CodeMethod.Text ? maskPhoneNumber(this._phoneNumber) : this._email}`;
    return html` <div class="codeSent" role="status">
      <span
        class=${`codeSentIcon--${this._method}`}
        title=${this._method}
        role="presentation"
      ></span>
      <span class="codeSentText">${text}</span>
    </div>`;
  }
  _renderShadow() {
    return html` <div
        class="shadow primary"
        aria-hidden="true"
        @click=${this._handleClose}
      ></div>
      <div class="shadow secondary">
        <slot name="secondary-shadow"></slot>
      </div>`;
  }
  async _sendCode() {
    var _a;
    const result = await this._codeRequest.run({
      method: this._method,
      email: this._email,
      channelId: this._channelId
    });
    if (!result.success) {
      const error = new VerifyRequestCodeError("Failed to request verification code", {
        cause: (_a = result.error) != null ? _a : void 0
      });
      this._emit("error", { error });
    } else {
      this._emit("sendCode", {
        method: this._method,
        recipient: this._method === CodeMethod.Email ? this._email : maskPhoneNumber(this._phoneNumber),
        resent: this._hasSent
      });
      this._hasSent = true;
    }
    return result;
  }
  async _login() {
    var _a;
    const result = await this._loginRequest.run({
      verificationMethod: this._method,
      email: this._email,
      passcode: this._code,
      provider: this._loginProviderSlug,
      url: this._loginUrl
    });
    if (result.status && result.status >= 500) {
      const error = new VerifySubmitCodeError("Failed to complete code verification", {
        cause: (_a = result.error) != null ? _a : void 0
      });
      this._emit("error", { error });
    } else if (result.success) {
      this._emit("done");
    }
    return result;
  }
  _handleClose() {
    this.hide();
  }
  _handleResend() {
    this._sendCode();
  }
  _handleToggle() {
    this._method = this._method === CodeMethod.Email ? CodeMethod.Text : CodeMethod.Email;
    this._sendCode();
  }
  _emit(name, detail) {
    this.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        composed: true,
        detail
      })
    );
  }
  createRenderRoot() {
    var _a;
    return ((_a = window.olo) == null ? void 0 : _a.isTest) ? this : super.createRenderRoot();
  }
};
_createMfaComponent = /* @__PURE__ */ new WeakSet();
createMfaComponent_fn = function() {
  const verifyCode = async (code) => {
    this._code = code;
    await this._login();
  };
  const component = new OloCodeVerification({ onCodeVerification: verifyCode });
  component.slot = "input";
  return component;
};
VerifyAccountOverlay$1.styles = styles$2;
__decorateClass$2([
  property({ type: String, reflect: true })
], VerifyAccountOverlay$1.prototype, "data-test-oloAuthPrompt", 2);
__decorateClass$2([
  property({ type: String, reflect: true })
], VerifyAccountOverlay$1.prototype, "data-testid", 2);
__decorateClass$2([
  property({ type: Boolean, reflect: true })
], VerifyAccountOverlay$1.prototype, "open", 2);
__decorateClass$2([
  property()
], VerifyAccountOverlay$1.prototype, "useSeparatedInputs", 2);
__decorateClass$2([
  state()
], VerifyAccountOverlay$1.prototype, "customStyles", 2);
__decorateClass$2([
  state()
], VerifyAccountOverlay$1.prototype, "_channelId", 2);
__decorateClass$2([
  state()
], VerifyAccountOverlay$1.prototype, "_email", 2);
__decorateClass$2([
  state()
], VerifyAccountOverlay$1.prototype, "_method", 2);
__decorateClass$2([
  state()
], VerifyAccountOverlay$1.prototype, "_phoneNumber", 2);
__decorateClass$2([
  state()
], VerifyAccountOverlay$1.prototype, "_loginUrl", 2);
__decorateClass$2([
  state()
], VerifyAccountOverlay$1.prototype, "_loginProviderSlug", 2);
__decorateClass$2([
  state()
], VerifyAccountOverlay$1.prototype, "_code", 2);
__decorateClass$2([
  state()
], VerifyAccountOverlay$1.prototype, "_disabled", 2);
__decorateClass$2([
  state()
], VerifyAccountOverlay$1.prototype, "_codeRequest", 2);
__decorateClass$2([
  state()
], VerifyAccountOverlay$1.prototype, "_loginRequest", 2);
__decorateClass$2([
  state()
], VerifyAccountOverlay$1.prototype, "_cleanup", 2);
__decorateClass$2([
  state()
], VerifyAccountOverlay$1.prototype, "_verifyRef", 2);
__decorateClass$2([
  state()
], VerifyAccountOverlay$1.prototype, "_arrowRef", 2);
__decorateClass$2([
  state()
], VerifyAccountOverlay$1.prototype, "_targetEl", 2);
__decorateClass$2([
  state()
], VerifyAccountOverlay$1.prototype, "_hasSent", 2);
VerifyAccountOverlay$1 = __decorateClass$2([
  customElement("verify-account-overlay")
], VerifyAccountOverlay$1);
const styles$1 = css`
  :host {
    align-items: center;
    display: inline-flex;
  }

  .toggle {
    background: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABcAAAAZCAYAAADaILXQAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAKVSURBVHgBrZXdS1NhHMe/z9mmVuqahlhJRGAXpVPCQrrwsqv6G7qpyIIIgl6hLC+Kit6WWrkug/IiiiAqISvojepCh9AMtSY21I2597O5nZ+/bU6YnLMXt+/hHM45v+f3eb7P73mecwRWobcOajLEcUAfg7WjUcxptRMoQK//UHW5Dqc56RQBFSC4DDqYO7YKp1p7KR9oVxdJgxN0pEyHMX68kASnrNX6FZRp5emzQYlIDP3FHob1sstdKFCazt/8o53vJ/FMIXxaDTghVeeD42TUxfGNHVcWNCkrpOpcr6CKaKmupYTvsFLNzR84zK51KFIZZTH30iGK4JJPQgM7hyimJlhyztD2ph4aYl4/L68GOQa8mwAi8dyAB9/Rae6jOrWYYOhedvhZLWjkqu9vBNo2qY+CzeDKR8ATxjyHL48cE3cynHOSARryysATG/DwF+DwIpvWc0e3m3tpuKWH9i3DbZ3JddzN936tzN8u4NZXYGAUmJeRTWYFeLW9jzYn4Txesh0XF3lYu7mTx3xqVvrLVKoT3lyYCwFPbcmSZEqo3qbUbKFm7vIRR9pRqAjTfL3GZu+rwhMynrVvW2Mot5tq6/X6snLkIR+f/Tx/10c6xWz6per2jwblaFyEEfT5UFO3EZVGEyRJc099UCScHD0qhlcGcnwVFbhnpuHzuGCsrUNVtWl5TS5EZLhm/yuBoOegfK/NoZafFZ7WQjQCl3MKAa8Hpg31CIcC8LpnEp/krH+bvOBpyQx1OsYTY0ocIUF4URGBN1wKeEqUII8JiPMBS+vzbC0LgvMunJSIrvotrVYuPuVqny/cRwIDcYp1By3qk5c3PKwPutfF194QRGfY4UshiXOBuy12lFIVJ35uQRFaBLRs98IPGRqJAAAAAElFTkSuQmCC')
      no-repeat center center;
    border: 0;
    cursor: pointer;
    height: 36px;
    padding: 0;
    width: 36px;
  }
`;
var __defProp$1 = Object.defineProperty;
var __getOwnPropDesc$1 = Object.getOwnPropertyDescriptor;
var __decorateClass$1 = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc$1(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp$1(target, key, result);
  return result;
};
let VerifyAccountOverlayToggle = class extends LitElement {
  constructor() {
    super(...arguments);
    this["data-test-oloAuthToggle"] = "";
    this["data-testid"] = "oloAuthToggle";
    this.title = "Olo Pay";
  }
  async attach(el) {
    this.detach();
    el.after(this);
  }
  async detach() {
    var _a;
    (_a = this.parentElement) == null ? void 0 : _a.removeChild(this);
  }
  render() {
    return html` <button class="toggle" type="button" title="${this.title}"></button>`;
  }
  createRenderRoot() {
    var _a;
    return ((_a = window.olo) == null ? void 0 : _a.isTest) ? this : super.createRenderRoot();
  }
};
VerifyAccountOverlayToggle.styles = styles$1;
__decorateClass$1([
  property({ type: String, reflect: true })
], VerifyAccountOverlayToggle.prototype, "data-test-oloAuthToggle", 2);
__decorateClass$1([
  property({ type: String, reflect: true })
], VerifyAccountOverlayToggle.prototype, "data-testid", 2);
__decorateClass$1([
  property({ type: String })
], VerifyAccountOverlayToggle.prototype, "title", 2);
VerifyAccountOverlayToggle = __decorateClass$1([
  customElement("verify-account-overlay-toggle")
], VerifyAccountOverlayToggle);
const emailCheck = /(.+)@(.+){2,}\.(.+){2,}/;
const checkForUser = async (email, onRun, onFound, onError) => {
  var _a, _b, _c, _d;
  if (!emailCheck.test(email))
    return;
  const req = await new RequestStatus(findUser).run({ email }, void 0, true);
  const found = !!req.success;
  onRun == null ? void 0 : onRun(found);
  if (req.status && req.status >= 500) {
    const error = new VerifyCheckUserError("Failed to run find user", {
      cause: req.error || void 0
    });
    onError == null ? void 0 : onError(error);
  }
  onFound((_b = (_a = req.response) == null ? void 0 : _a.loginHint) != null ? _b : null, email, (_d = (_c = req.response) == null ? void 0 : _c.phoneNumber) != null ? _d : "");
};
const checkForUserDebounced = lodash_debounce(checkForUser, 400);
const addDetector = async ({
  el,
  apiUrl,
  loginUrl,
  initialValue = "",
  callbacks,
  channelId,
  loginProviderSlug,
  fetchMethod: fetchMethod2
}, overlay = new VerifyAccountOverlay$1()) => {
  var _a, _b;
  if (!channelId) {
    const error = new VerifyChannelIdError(
      "Olo Auth requires a channelId to be specified!"
    );
    (_a = callbacks == null ? void 0 : callbacks.onError) == null ? void 0 : _a.call(callbacks, error);
    return Promise.resolve();
  }
  if (!loginProviderSlug) {
    const error = new VerifyLoginProviderSlugError(
      "Olo Auth requires a loginProviderSlug to be specified!"
    );
    (_b = callbacks == null ? void 0 : callbacks.onError) == null ? void 0 : _b.call(callbacks, error);
    return Promise.resolve();
  }
  let removed = false;
  el = el.matches("input") ? el : el.querySelector("input");
  if (apiUrl) {
    setDefaultApiUrl(apiUrl);
  }
  if (fetchMethod2) {
    setFetchMethod(fetchMethod2);
  }
  const handleSendCode = (e) => {
    var _a2;
    (_a2 = callbacks == null ? void 0 : callbacks.onSendCode) == null ? void 0 : _a2.call(callbacks, e.detail.method, e.detail.recipient, e.detail.resent);
  };
  const handleOpen = (_) => {
    var _a2;
    (_a2 = callbacks == null ? void 0 : callbacks.onVerifyOpen) == null ? void 0 : _a2.call(callbacks);
  };
  const handleClose = (_) => {
    var _a2;
    (_a2 = callbacks == null ? void 0 : callbacks.onVerifyClose) == null ? void 0 : _a2.call(callbacks);
  };
  const handleLoading = (_) => {
    var _a2;
    (_a2 = callbacks == null ? void 0 : callbacks.onVerifyLoading) == null ? void 0 : _a2.call(callbacks);
  };
  const handleDone = (_) => {
    var _a2;
    (_a2 = callbacks == null ? void 0 : callbacks.onVerifyDone) == null ? void 0 : _a2.call(callbacks);
  };
  const handleError = (e) => {
    var _a2;
    (_a2 = callbacks == null ? void 0 : callbacks.onError) == null ? void 0 : _a2.call(callbacks, e.detail.error);
  };
  overlay.addEventListener("sendCode", handleSendCode);
  overlay.addEventListener("open", handleOpen);
  overlay.addEventListener("close", handleClose);
  overlay.addEventListener("loading", handleLoading);
  overlay.addEventListener("done", handleDone);
  overlay.addEventListener("error", handleError);
  const toggle = new VerifyAccountOverlayToggle();
  const handleToggle = (_) => {
    if (overlay.open) {
      overlay.hide();
    } else {
      overlay.show();
    }
  };
  toggle.addEventListener("click", handleToggle);
  const handleUserFound = (userId, email, phoneNumber) => {
    if (removed)
      return;
    if (userId) {
      toggle.attach(el);
      overlay.attach(toggle, {
        email,
        phoneNumber,
        channelId,
        loginUrl,
        loginProviderSlug
      });
    } else {
      overlay.detach();
      toggle.detach();
    }
  };
  const handleInputChange = (e) => {
    if (removed)
      return;
    checkForUserDebounced(
      e.target.value.trim() || "",
      callbacks == null ? void 0 : callbacks.onEmailCheck,
      handleUserFound,
      callbacks == null ? void 0 : callbacks.onError
    );
  };
  el.addEventListener("input", handleInputChange);
  if (initialValue) {
    checkForUser(
      initialValue,
      callbacks == null ? void 0 : callbacks.onEmailCheck,
      handleUserFound,
      callbacks == null ? void 0 : callbacks.onError
    );
  }
  return async () => {
    removed = true;
    el.removeEventListener("input", handleInputChange);
    overlay.removeEventListener("sendCode", handleSendCode);
    overlay.removeEventListener("open", handleOpen);
    overlay.removeEventListener("close", handleClose);
    overlay.removeEventListener("done", handleDone);
    overlay.detach();
    toggle.detach();
  };
};
class VerifyAccountOverlay2 {
  async attachEmailListener(props) {
    var _a;
    const overlay = new VerifyAccountOverlay$1({
      useSeparatedInputs: (_a = props.useSeparatedInputs) != null ? _a : false
    });
    if (this.customStyles) {
      overlay.setStyles(this.customStyles);
    }
    this.removeDetector = await addDetector(props, overlay);
  }
  async detachEmailListener() {
    var _a;
    (_a = this.removeDetector) == null ? void 0 : _a.call(this);
  }
  setStyles(customStyles) {
    this.customStyles = customStyles;
  }
}
const styles = css`
  #sign-in-button-container {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 4.8px;
  }

  #sign-in-button {
    display: flex;
    background-color: #2d96ff;
    border-color: #2d96ff;
    border-width: 1px;
    color: #ffffff;
    font-family: Helvetica, Arial, sans-serif;
    font-weight: 400;
    justify-content: center;
    cursor: pointer;
    line-height: 1;
    font-size: 16px;
    border-radius: 3.2px;
    align-items: center;
    transition: all 200ms;
    --highlight-color: #2d96ff;
  }

  #sign-in-button:hover {
    background-color: #006ad1;
    border-color: #006ad1;
    box-shadow: 0 0px 6px 0 rgba(0, 0, 0, 0.25);
  }

  #sign-in-button:active {
    background-color: #005fbc;
    border-color: #005fbc;
    box-shadow-color: #2d96ff;
    color: #ffffff;
  }

  span {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
  }

  #olo-logo {
    height: 40px;
    width: 40px;
    margin-left: 0px;
    padding-left: 6.4px;
  }

  #info-icon-container {
    height: 19.2px;
    width: 19.2px;
    cursor: pointer;
    fill: #2d96ff;
    border: none;
    background: none;
    padding: 0px;
    position: relative;
    display: inline-block;
  }

  #info-icon-container #info-tooltip {
    visibility: hidden;
    width: 160px;
    background-color: #ffffff;
    border: 1px solid #dadada;
    color: #141414;
    text-align: left;
    padding: 11.2px;
    position: absolute;
    z-index: 99;
    bottom: 200%;
    left: 50%;
    margin-left: -91.2px;
    font-family: Helvetica, Arial, sans-serif;
    font-weight: normal;
    letter-spacing: normal;
    line-height: 1.5;
    text-transform: none;
    font-size: 14px;
  }

  #info-icon-container #info-tooltip::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: #dadada transparent transparent transparent;
  }

  #info-icon-container:hover #info-tooltip {
    visibility: visible;
  }

  #info-icon-container:focus #info-tooltip {
    visibility: visible;
  }

  #info-img {
    width: 100%;
    height: 100%;
  }
`;
const logo = "data:image/svg+xml;base64,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";
const info = "data:image/svg+xml;base64,ICA8c3ZnIHZpZXdCb3g9IjAgMCAyNCAyNCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICAgIDxwYXRoIGQ9Ik0xMiAyQzYuNDggMiAyIDYuNDggMiAxMnM0LjQ4IDEwIDEwIDEwIDEwLTQuNDggMTAtMTBTMTcuNTIgMiAxMiAyem0xIDE1aC0ydi02aDJ2NnptMC04aC0yVjdoMnYyeiIgZmlsbD0iIzJkOTZmZiIvPgogIDwvc3ZnPgo=";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
let SignInButton = class extends LitElement {
  constructor() {
    super(...arguments);
    this.apiUrl = "";
    this.redirectUri = "";
    this.channelId = "";
    this.clientId = "";
    this.state = void 0;
    this.customWidth = void 0;
    this.customHeight = void 0;
    this.hideHeader = false;
    this._handleButtonClick = (composedRedirectUrl) => {
      window.location.replace(composedRedirectUrl);
    };
  }
  render() {
    var _a, _b;
    const getOloEnvUrl = this.apiUrl.split("/api");
    const composedRedirectUrl = `${getOloEnvUrl[0]}/connect/authorize?scope=openid+profile+email+phone+offline_access&response_type=code&prompt=login&redirect_uri=${this.redirectUri}&client_id=${this.clientId}&olo_channel_id=${this.channelId}&hide_header=${this.hideHeader}${this.state ? `&state=${this.state}` : ""}`;
    const buttonSize = html`
      <style>
        #sign-in-button {
          height: ${this.customHeight ? this.customHeight : "53px"};
          width: ${this.customWidth ? this.customWidth : "304px"};
        }

        ${!this.customHeight && !this.customWidth ? `@media screen and (max-width: 768px) {
          #sign-in-button {
            width:60%;
            font-size: 1.15rem;
            letter-spacing:.6px;
          }
        }` : ""}
      </style>
    `;
    return html`
      ${buttonSize}
      <div id="sign-in-button-container">
        <button
          id="sign-in-button"
          aria-label="${"Sign in with"} Olo"
          @click=${() => this._handleButtonClick(composedRedirectUrl)}
        >
          <span>
            ${"Sign in with"}
            <img id="olo-logo" alt="olo" src=${logo} />
          </span>
        </button>
        <button id="info-icon-container">
          <img
            id="info-img"
            alt="info"
            aria-label="${(_a = this.cdnMessaging) == null ? void 0 : _a.description}"
            src=${info}
          />
          <span id="info-tooltip"> ${(_b = this.cdnMessaging) == null ? void 0 : _b.description} </span>
        </button>
      </div>
    `;
  }
  async connectedCallback() {
    super.connectedCallback();
    const cdnFetch = await fetch(
      "https://static.olocdn.net/web-client/olo-auth-service-app/signInButtonMessaging.json"
    );
    const cdnMessaging = await cdnFetch.json();
    this.cdnMessaging = cdnMessaging;
    this.render();
  }
};
SignInButton.styles = [styles];
__decorateClass([
  property({ type: Object })
], SignInButton.prototype, "cdnMessaging", 2);
__decorateClass([
  property()
], SignInButton.prototype, "apiUrl", 2);
__decorateClass([
  property()
], SignInButton.prototype, "redirectUri", 2);
__decorateClass([
  property()
], SignInButton.prototype, "channelId", 2);
__decorateClass([
  property()
], SignInButton.prototype, "clientId", 2);
__decorateClass([
  property()
], SignInButton.prototype, "state", 2);
__decorateClass([
  property()
], SignInButton.prototype, "customWidth", 2);
__decorateClass([
  property()
], SignInButton.prototype, "customHeight", 2);
__decorateClass([
  property()
], SignInButton.prototype, "hideHeader", 2);
SignInButton = __decorateClass([
  customElement("sign-in-button")
], SignInButton);
export {
  CreateAccountCheckbox,
  OloCodeVerification,
  SignInButton,
  VerifyAccountOverlay2 as VerifyAccountOverlay,
  VerifyAttachError,
  VerifyChannelIdError,
  VerifyCheckUserError,
  VerifyDetachError,
  VerifyHideError,
  VerifyRenderError,
  VerifyRequestCodeError,
  VerifyShowError,
  VerifySubmitCodeError
};
